import classnames from 'classnames';
import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React from 'react';
import 'prop-types';
import merge from 'lodash/merge';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import DOMPurify from 'dompurify';
const constants = {
  DESKTOP_MAX_CHARS: 750,
  MOBILE_MAX_CHARS: 250,
  MOBILE_QADB_MAX_CHARS: 260,
  onlyLetterAndHyphenRegex: '[a-zA-Z-]+',
  naturalUrlPIRegex: '([0-9]+(-(*))?)-(([u|U][d|D][a|A])|([n|N][v|V][a|A]))',
  itemOrProductIdRegex: '[m|M][a-zA-Z]{2}[u|U]?[0-9]+',
  itemOrProductIdByGroupRegex: '([m|M][a-zA-Z]{2}[u|U]?)([0-9]+)',
  vipItemIdAndTitleRegex: '([m|M][a-zA-Z]{2}[u|U]?)-([0-9]+)(*)',
  vipItemIdRegex: '([m|M][a-zA-Z]{2})-([0-9]+)',
  pdpItemIdRegex: '([m|M][a-zA-Z]{2})([0-9]+)',
  upItemIdRegex: '([m|M][a-zA-Z]{2}[u|U])([0-9]+)',
  vipItemIdFromUrl: '(/)([m|M][a-zA-Z]{2})-([0-9]+)',
  pdpItemIdFromUrl: '(/[p|P]/)([m|M][a-zA-Z]{2})([0-9]+)',
  upItemIdFromUrl: '(/up/)([m|M][a-zA-Z]{2}[u|U])([0-9]+)',
  quotationIdRegex: '[0-9]+',
  // Source https://github.com/mercadolibre/melidata-analysis/blob/develop/src/main/scala/melidata/analysis/jobs/Functions.scala#L317
  isBotRegexUserAgent: /(bot|curl|postman|node|python|crawl|wget|lwp|bbbike|java|spider|slurp|feedburner|googleimageproxy|google web preview|whatsapp|riddler|scrapy|facebookexternalhit|ruby|nuclei|vivaldi|mediapartners-google|apis-google|feedfetcher-google|duplexweb-google|google vavicon|googleweblight|go-http-client)/i,
  isTrustedBotsRegexUserAgent: /(googlebot|bingbot|AdsBot-Google)/i,
  isBotRegexReferer: /robots\.txt/i,
  userAgentFacebookRegex: /FBAN\/FBIOS|FB/,
  userAgentInstagramRegex: /Instagram/,
  userAgentTwitterRegex: /Twitter/,
  refererTwitter: 'https://t.co/',
  refererYoutube: 'https://www.youtube.com/',
  forceQueryParam: 'force_app',
  modals: {
    WARRANTY: 'warranty',
    RETURNS: 'returns',
    CBT: 'cbt',
    CBT_TAXES: 'cbt_taxes',
    SHIPPING: 'shipping',
    PAYMENTS: 'payments',
    PAYMENTS_NO_MP: 'payments_no_mp',
    CREDITS_ACTIVATION: 'credits_activation',
    CREDITS_ACQUISITION: 'credits_acquisition',
    ADDITIONAL_INFO: 'additional_info',
    M_COINS: 'm_coins',
    LOYALTY: 'loyalty',
    BUY_PROTECTED: 'buy_protected',
    FCUSTAXES: 'fcus_taxes',
    SHIPPING_CALCULATOR: 'shipping_calculator',
    VEHICLE_CONDITION_DETAILS: 'vehicle_condition_details',
    PRODUCTS_VARIATION: 'buy-multiple-variations'
  },
  devices: {
    MOBILE: 'mobile',
    DESKTOP: 'desktop'
  },
  actionTypes: {
    LINK: 'link',
    BUTTON: 'button'
  },
  widgetTypes: {
    acquisition: 'acquisition',
    activation: 'activation',
    splitPayments: 'split_payment_methods'
  },
  osName: {
    ANDROID: 'android',
    IOS: 'ios'
  },
  icon: {
    RIGHT: 'right',
    LEFT: 'left'
  },
  INSURTECH_SHOWED: 'showed',
  INSURTECH_NO_WARRANTY_SELECTED: 'insurance_option_row_0',
  UUID_FLEXIBLE_REGEX: '^\\w{8}-\\w{4}-\\w{4}-\\w{4}-\\w{12}',
  D2ID_REGEX: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(-n|-f|-s)?$/i,
  MSHOPS_D2ID_SUFFIX: '-s',
  MSHOPS_D2ID_LENGTH: 36,
  UTM_PARAMS: ['utm_campaign', 'utm_source', 'utm_medium', 'utm_term', 'utm_content'],
  mshops: {
    SITE: 'MS',
    GUEST_ADDITIONAL_INFO: 'ui-pdp-additional-info__link',
    CHO_GUEST_STYLE: 'cho-guest',
    HAVE_MELI_SESSION: 'false',
    CHANNEL: 'mshops',
    SHIPPING_COMPONENT: 'shipping_summary',
    WITH_PROMISE: 'with_promise',
    DEFAULT_PROMISE: 'without_promise',
    WITH_COACH: 'with_coachmark',
    DEFAULT_COACHMARK: 'without_coachmark',
    ACCEPT_BUTTON_ACTION: 'accept',
    CHANGE_LOCATION_ACTION: 'change_location',
    VERIFY_HEADERS: ['x-d2id', 'x-push-tooltip-config']
  },
  QUALTRICS_ID: 'ZN_efi6I3nkl8bGcTA',
  API_BASE_PATH: '/p/api/',
  DEVICE_TYPE: {
    DESKTOP: 'desktop',
    TABLET: 'tablet',
    MOBILE: 'mobile'
  },
  VISIBILITY: {
    VISIBLE: 'VISIBLE',
    HIDDEN: 'HIDDEN'
  },
  LAYOUT: {
    PDP: 'pdp',
    UPP: 'upp',
    VIP_CORE: 'vip-core',
    VIP_MOTORS: 'vip-motors',
    VIP_SERVICES: 'vip-services',
    VIP_REAL_ESTATE: 'vip-real-estate',
    SHOPS_PDP: 'shops-pdp',
    SHOPS_UPP: 'shops-upp',
    SHOPS_VIP_CORE: 'shops-vip-core',
    PORTAL_INMOBILIARIO: 'vip-real-estate-ltr-multifamily',
    QUOTATION: 'vip-real-estate-devs-quotable'
  }
};
const SIZES = {
  XXXSMALL: 10,
  XXSMALL: 12,
  XSMALL: 14,
  SMALL: 16,
  MEDIUM: 18,
  LARGE: 20,
  XLARGE: 24,
  XXLARGE: 28
};
const WEIGHTS = {
  LIGHT: 'LIGHT',
  REGULAR: 'REGULAR',
  SEMIBOLD: 'SEMIBOLD',
  BOLD: 'BOLD'
};
const ANDES = {
  SIZES,
  WEIGHTS
};

/**
 * addEllipsisEnd
 * ________________
 * Se le agregan 3 puntos suspensivos al texto.
 * Para generar consistencia visual se borran los puntos finales del mismo antes de concatenar el string '...'
 * Se contempla los casos en donde el string termina con un espacio. (Se borra)
 */

const addEllipsisEnd = rawText => {
  const text = rawText.trimEnd();
  if (text.endsWith('.')) {
    return addEllipsisEnd(text.slice(0, text.length - 1));
  }
  return `${text}...`;
};
const DEFAULT_SIZE = false;
const namespace$5 = 'ui-pdp-size';
const sizenames = (size, className) => {
  const sizeName = typeof size === 'string' ? size : (size === null || size === void 0 ? void 0 : size.font_size) || DEFAULT_SIZE;
  return classnames({
    [`${namespace$5}--${sizeName}`]: sizeName
  }, className);
};
const DEFAULT_COLOR = false;
const namespace$4 = 'ui-pdp-color';
const colornames = (color, className) => {
  const colorName = typeof color === 'string' ? color : (color === null || color === void 0 ? void 0 : color.color) || DEFAULT_COLOR;
  return classnames({
    [`${namespace$4}--${colorName}`]: colorName
  }, className);
};
const DEFAULT_FAMILY = false;
const namespace$3 = 'ui-pdp-family';
const familynames = (family, className) => {
  const familyName = typeof family === 'string' ? family : (family === null || family === void 0 ? void 0 : family.font_family) || DEFAULT_FAMILY;
  return classnames({
    [`${namespace$3}--${familyName}`]: familyName
  }, className);
};
const DEFAULT_BG_COLOR = false;
const namespace$2 = 'ui-pdp-background-color';
const backgroundColor = (bgColor, className) => {
  const colorName = typeof bgColor === 'string' ? bgColor : bgColor && (bgColor.bgColor || bgColor.background_color) || DEFAULT_BG_COLOR;
  return classnames({
    [`${namespace$2}--${colorName}`]: colorName
  }, className);
};
const namespace$1 = 'ui-vpp-text-alignment';
const textAlignment = (alignment, className) => classnames({
  [`${namespace$1}--${alignment}`]: alignment
}, className);
const namespace = 'm';
const margins = (margin, className) => {
  if (!margin) {
    return className;
  }
  const {
    top,
    bottom,
    left,
    right
  } = margin;
  return classnames({
    [`${namespace}t-${top}`]: top,
    [`${namespace}b-${bottom}`]: bottom,
    [`${namespace}l-${left}`]: left,
    [`${namespace}r-${right}`]: right
  }, className);
};
const applyClasses = _ref => {
  let {
    alignment,
    size,
    family,
    color,
    bg_color,
    margin,
    className
  } = _ref;
  return textAlignment(alignment, backgroundColor(bg_color, colornames(color, sizenames(size, familynames(family, margins(margin, className))))));
};
const {
  mshops: mshops$1
} = constants;
const {
  WITH_COACH,
  DEFAULT_COACHMARK,
  WITH_PROMISE,
  DEFAULT_PROMISE
} = mshops$1;
const getShippingCoachMarkTrack = shippingCoachMark => shippingCoachMark ? WITH_COACH : DEFAULT_COACHMARK;
const getPromiseTrack = haveCpCookie => haveCpCookie ? WITH_PROMISE : DEFAULT_PROMISE;
const tooltipIsVisible = actionTooltip => (actionTooltip === null || actionTooltip === void 0 ? void 0 : actionTooltip.visible) && (actionTooltip === null || actionTooltip === void 0 ? void 0 : actionTooltip.id) && (actionTooltip === null || actionTooltip === void 0 ? void 0 : actionTooltip.content);
const getEventClickShippingCalculateTrack = (page, withCoach, withPromise) => ({
  melidata_event: {
    path: `/${page}/shipping_calculate`,
    event_data: {
      category: page,
      action: 'shipping_calculate',
      experiment6: getShippingCoachMarkTrack(withCoach),
      experiment7: getPromiseTrack(withPromise)
    }
  },
  analytics_event: {
    section: `/${page.toUpperCase()}/SHIPPING_CALCULATE`,
    action: 'SHIPPING_CALCULATE',
    category: page.toUpperCase(),
    custom_dimensions: {
      28: getShippingCoachMarkTrack(withCoach).toUpperCase(),
      29: getPromiseTrack(withPromise).toUpperCase()
    }
  }
});
const getEventClickShippingValuesTrack = (page, action, withCoach, withPromise) => ({
  melidata_event: {
    path: `/current_location/shipping_values/${action.toLowerCase()}.`,
    event_data: {
      action: action.toLowerCase(),
      category: 'current_location',
      label: page.toLowerCase(),
      experiment6: getShippingCoachMarkTrack(withCoach),
      experiment7: getPromiseTrack(withPromise)
    }
  },
  analytics_event: {
    section: `/CURRENT_LOCATION/SHIPPING_VALUES`,
    action: action.toUpperCase(),
    category: 'CURRENT_LOCATION',
    label: page.toUpperCase(),
    custom_dimensions: {
      28: getShippingCoachMarkTrack(withCoach).toUpperCase(),
      29: getPromiseTrack(withPromise).toUpperCase()
    }
  }
});
const getCoachTooltipsProps = (actionTooltip, className) => ({
  id: actionTooltip === null || actionTooltip === void 0 ? void 0 : actionTooltip.id,
  className,
  trigger: 'no-trigger',
  side: 'left',
  type: 'highlight',
  content: actionTooltip === null || actionTooltip === void 0 ? void 0 : actionTooltip.content,
  closable: true,
  offsetY: 32
});
const getCoachCardTooltipsProps = actionTooltip => {
  if (!tooltipIsVisible(actionTooltip)) {
    return null;
  }
  return {
    id: actionTooltip === null || actionTooltip === void 0 ? void 0 : actionTooltip.id,
    title: {
      text: actionTooltip === null || actionTooltip === void 0 ? void 0 : actionTooltip.content
    },
    has_shadow: true,
    closeable: true,
    storable: true,
    arrow_type: 'LEFT',
    highlight: true
  };
};
const updateCookieCompats = (req, res, compats_user_selection, domain) => {
  if (compats_user_selection) {
    res.cookie('main_attributes', compats_user_selection, {
      domain: `.${req.platform.domain}`,
      secure: true
    });
    res.cookie(`compats-domain-${domain}`, compats_user_selection, {
      domain: `.${req.platform.domain}`,
      secure: true
    });
    res.cookie('compats-widgetSearch', true, {
      domain: `.${req.platform.domain}`,
      secure: true
    });
  }
};
const resetCookieCompats = (req, res, domain) => {
  res.cookie('main_attributes', '', {
    domain: `.${req.platform.domain}`,
    secure: true
  });
  res.cookie(`compats-domain-${domain}`, '', {
    domain: `.${req.platform.domain}`,
    secure: true
  });
  res.cookie('compats-widgetSearch', true, {
    domain: `.${req.platform.domain}`,
    secure: true
  });
};
const _excluded = ["type", "id", "components", "useId", "state"];
const ComponentByKey = _ref => {
  let {
      type = null,
      id = null,
      components,
      useId = false,
      state = null
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  let ComponentByKeyComponent = null;
  if (type && !useId) {
    ComponentByKeyComponent = components[type.toLowerCase()];
  }
  if (id && useId) {
    ComponentByKeyComponent = components[id.toLowerCase()];
  }
  if (!type || !id && useId || !ComponentByKeyComponent || state === 'HIDDEN') {
    return null;
  }
  return /*#__PURE__*/React.createElement(ComponentByKeyComponent, _extends({
    type: type,
    id: id,
    state: state
  }, props));
};
const {
  VISIBILITY
} = constants;
const isOfficialStoreHeaderVisible = _ref => {
  let {
    header_store,
    header_top_brand_vip,
    header_official_store,
    header_top_brand_up
  } = _ref;
  const headerStore = header_store && header_store.state !== VISIBILITY.HIDDEN;
  const headerOfficialStore = header_official_store && header_official_store.state !== VISIBILITY.HIDDEN;
  const headerTopBrandFixedVip = header_top_brand_vip && header_top_brand_vip.state !== VISIBILITY.HIDDEN;
  const headerTopBrandFixedUp = header_top_brand_up && header_top_brand_up.state !== VISIBILITY.HIDDEN;
  const isVisible = headerStore || headerTopBrandFixedVip || headerOfficialStore || headerTopBrandFixedUp;
  return !!isVisible;
};
const isReviewsRowVisible = _ref2 => {
  let {
    reviews,
    reviews_capability_v3
  } = _ref2;
  const review = reviews && reviews.state !== VISIBILITY.HIDDEN;
  const reviewsV3 = reviews_capability_v3 && reviews_capability_v3.state !== VISIBILITY.HIDDEN;
  const isVisible = review || reviewsV3;
  return isVisible;
};
const isPaymentMethodsVisible = _ref3 => {
  let {
    returns_box,
    warranty,
    payment_methods
  } = _ref3;
  const hasReturnsBox = returns_box && returns_box.state !== VISIBILITY.HIDDEN;
  const hasWarranty = warranty && warranty.state !== VISIBILITY.HIDDEN;
  const hasPaymentMethods = payment_methods && payment_methods.state !== VISIBILITY.HIDDEN;
  const isVisible = hasReturnsBox || hasWarranty || hasPaymentMethods;
  return isVisible;
};
const isSellerInfoVisible = _ref4 => {
  let {
    seller_info
  } = _ref4;
  const hasSellerInfo = seller_info && seller_info.state !== VISIBILITY.HIDDEN;
  const isVisible = hasSellerInfo;
  return isVisible;
};
const isSellerDataVisible = _ref5 => {
  let {
    seller_data
  } = _ref5;
  const hasSellerData = seller_data && seller_data.state !== VISIBILITY.HIDDEN;
  const isVisible = hasSellerData;
  return isVisible;
};
function compose() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }
  if (fns.length === 0) {
    return arg => arg;
  }
  if (fns.length === 1) {
    return fns[0];
  }
  return fns.reduce((a, b) => function () {
    return a(b(...arguments));
  });
}
const getCookie = name => {
  // eslint-disable-next-line security/detect-non-literal-regexp
  const match = new RegExp(`(^|;\\s*)(${name})=([^;]*)`).exec(document.cookie);
  if (match) {
    // Try to parse the value if it has obj shape "{ key: value }"
    try {
      return {
        [name]: JSON.parse(match[3])
      };
    } catch (e) {
      return {
        [name]: match[3]
      };
    }
  }
  return null;
};
const setCookie = function (name, value) {
  let attrs = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  let cookieValue;
  if (typeof value === 'object') {
    const cookie = getCookie(name);
    cookieValue = JSON.stringify(cookie ? merge(cookie[name], value) : value);
  } else {
    cookieValue = value;
  }
  const keys = Object.keys(attrs);
  let cookieAttrs = '';
  if (keys.length > 0) {
    cookieAttrs = keys.reduce((acum, key) => `${acum};${key}=${attrs[key]}`, '');
  }
  document.cookie = `${name}=${cookieValue}${cookieAttrs}`;
};
const getCookieValue = name => {
  const value = getCookie(name);
  if (value) {
    return value[name];
  }
  return value;
};
const getExpiration = exdays => {
  const expFromNow = Date.now() + exdays * 24 * 60 * 60 * 1000;
  return new Date(expFromNow).toUTCString();
};
const corsOptionsDevelop = {
  origin: /^.+\.mercadoli(b|v)re\.(cl|com\.(ar|bo|br|co|cr|cu|do|ec|gt|mx|ni|hn|pa|pe|py|sv|ve|uy)|com).*$/,
  optionsSuccessStatus: 200,
  credentials: true
};
const corsOptions = {
  origin: /^.+\.mercadoli(b|v)re\.(cl|com\.(ar|bo|br|co|cr|cu|do|ec|gt|mx|ni|hn|pa|pe|py|sv|ve|uy)|com)$/,
  optionsSuccessStatus: 200,
  credentials: true
};

// Basado en https://github.com/albertodeago/curl-generator
// Se agrega soporte para queryParams y se corrige bug con Header que no son del tipo string

/**
 * @param {string} [method]
 * @returns {string}
 */
const getCurlMethod = method => {
  let result = '';
  if (method) {
    const types = {
      GET: '-X GET',
      POST: '-X POST',
      PUT: '-X PUT',
      PATCH: '-X PATCH',
      DELETE: '-X DELETE'
    };
    const methodUpperCased = method.toUpperCase();
    if (types[methodUpperCased]) {
      result = `${types[methodUpperCased]} `;
    } else {
      throw new Error(`Invalid method: "${method}"`);
    }
  }
  return result;
};

/**
 * @param {StringMap} headers
 * @param {StringMap} add
 * @param {array} toOfuscate
 * @param {array} toRemove
 * @returns {object}
 */
const headersHandler = (headers, _ref) => {
  let {
    add,
    toOfuscate,
    toRemove
  } = _ref;
  let headersData = headers;
  if (add) {
    headersData = _objectSpread(_objectSpread({}, headersData), add);
  }
  if (toOfuscate !== null && toOfuscate !== void 0 && toOfuscate.length) {
    // eslint-disable-next-line array-callback-return
    toOfuscate.map(key => {
      if (headersData[key]) {
        headersData[key] = '***';
      }
    });
  }
  if (toRemove !== null && toRemove !== void 0 && toRemove.length) {
    // eslint-disable-next-line array-callback-return
    toRemove.map(key => {
      if (headersData[key]) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete headersData[key];
      }
    });
  }
  return headersData;
};

/**
 * @param {StringMap} headers
 * @returns {string}
 */
const getCurlHeaders = (headers, options) => {
  var _options$handleHeader;
  let result = '';
  const headersData = headers && options !== null && options !== void 0 && (_options$handleHeader = options.handleHeaders) !== null && _options$handleHeader !== void 0 && _options$handleHeader.enabled ? headersHandler(headers, options.handleHeaders) : headers;
  if (headersData) {
    // eslint-disable-next-line no-warning-comments
    // TODO sacar el siguiente lint array-callback-return
    // eslint-disable-next-line array-callback-return
    Object.keys(headersData).map(val => {
      if (typeof headers[val] === 'boolean') {
        // eslint-disable-next-line no-useless-escape
        result += `-H \"${val}: ${headersData[val]}\" `;
      } else if (typeof headersData[val] === 'string') {
        // eslint-disable-next-line no-useless-escape
        result += `-H \"${val}: ${headersData[val].replace(/(\\|")/g, '\\$1'
        // eslint-disable-next-line no-useless-escape
        )}\" `;
      }
    });
  }
  return result;
};

/**
 * @param {Object} body
 * @returns {string}
 */
const getCurlBody = body => {
  const result = body ?
  // eslint-disable-next-line no-useless-escape
  `-d \"${JSON.stringify(body).replace(/(\\|")/g, '\\$1')}\" ` : '';
  return result;
};
const getCurlQueryParams = params => {
  const result = params ? `?${Object.keys(params).map(key => `${key}=${params[key]}`).join('&')}` : '';

  // const result = params ? '?' + Object.keys(params).map(key => key + '=' + params[key]).join('&') : '';
  return result;
};

/**
 * Given the curl additional options, turn them into curl syntax
 * @param {CurlAdditionalOptions} [options]
 * @returns {string}
 */
const getCurlOptions = options => {
  let result = '';
  if (options) {
    Object.keys(options).forEach(key => {
      const kebabKey = key.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
      if (!options[key]) {
        throw new Error(`Invalid Curl option: ${key}`);
      } else if (typeof options[key] === 'boolean' && options[key]) {
        // boolean option, we just add --opt
        result += `--${kebabKey} `;
      } else if (typeof options[key] === 'string' || typeof options[key] === 'number') {
        // eslint-disable-next-line no-warning-comments
        // TODO revisar el cas de "number"
        // string option, we have to add --opt=value
        result += `--${kebabKey} ${options[key]} `;
      }
    });
  }
  return result;
};

/**
 * @param {CurlRequest} params
 * @param {CurlAdditionalOptions} [options]
 * @returns {string}
 */
const CurlGenerator = (params, options) => {
  if (!params.baseURL) {
    params.baseURL = '';
  }
  let curlSnippet = 'curl ';

  // eslint-disable-next-line no-useless-escape
  curlSnippet += `\"${params.baseURL}${params.url}${getCurlQueryParams(params.queryParams
  // eslint-disable-next-line no-useless-escape
  )}\" `;
  curlSnippet += getCurlMethod(params.method);
  curlSnippet += getCurlHeaders(params.headers, params.handleHeaders);
  curlSnippet += getCurlBody(params.body);
  curlSnippet += getCurlOptions(options);
  return curlSnippet.trim();
};
// Only for testing purpouse
const privateMethodTest = {
  _getCurlOptions: getCurlOptions,
  _getCurlBody: getCurlBody,
  _getCurlMethod: getCurlMethod,
  _getCurlQueryParams: getCurlQueryParams,
  _getCurlHeaders: getCurlHeaders
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
/* global window */
const canUseDOM$1 = !!(typeof window !== 'undefined' && window.document && window.document.createElement);

/**
 * Returns true if the HTML5 history API is supported. Taken from Modernizr.
 *
 * https://github.com/Modernizr/Modernizr/blob/master/LICENSE
 * https://github.com/Modernizr/Modernizr/blob/master/feature-detects/history.js
 * changed to avoid false negatives for Windows Phones: https://github.com/reactjs/react-router/issues/586
 */
const supportsHistory = () => {
  if (!canUseDOM$1) {
    return false;
  }
  const ua = window.navigator.userAgent;
  if ((ua.includes('Android 2.') || ua.includes('Android 4.0')) && ua.includes('Mobile Safari') && !ua.includes('Chrome') && !ua.includes('Windows Phone')) {
    return false;
  }
  return window.history && 'pushState' in window.history;
};
const getLocationForHash = hash =>
// eslint-disable-next-line no-negated-condition
typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}${window.location.search}#${hash}` : null;
const supportsWebShareApi = () =>
// eslint-disable-next-line @typescript-eslint/unbound-method
typeof window !== 'undefined' && window.navigator && window.navigator.share;
const forEach = (array, callback, scope) => {
  for (let i = 0; i < array.length; i += 1) {
    callback.call(scope, array[i]);
  }
};
const doneHandler = (res, req) => (error, output) => {
  // eslint-disable-next-line no-negated-condition
  if (!error) {
    var _req$navigationPerfor, _req$navigationPerfor2;
    res.send(output);
    req === null || req === void 0 || (_req$navigationPerfor = req.navigationPerformance) === null || _req$navigationPerfor === void 0 || (_req$navigationPerfor2 = _req$navigationPerfor.stop) === null || _req$navigationPerfor2 === void 0 || _req$navigationPerfor2.call(_req$navigationPerfor);
  } else {
    res.status(500);
  }
};
const EXP_THREE_COLS_VIP_LAYOUT = 'vip-core-3-cols';
const EXP_UPP_LAYOUT = 'upp';
const internalIncludes = (el, search) => {
  try {
    return el.indexOf(search) !== -1;
  } catch (e) {
    return false;
  }
};
const arrayIncludes = internalIncludes;
const stringIncludes = internalIncludes;
const isIterable = obj => {
  // eslint-disable-next-line no-eq-null
  if (obj == null) {
    return false;
  }
  return typeof obj === 'object';
};

// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable no-restricted-syntax */
const search = (iterable, array) => {
  const result = array;
  for (const [key, value] of Object.entries(iterable)) {
    const valueId =
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    value && value.id ? `${value.id.toString().toLowerCase()}` : null;
    if ((key === 'icon' || key === 'text_icon') && !arrayIncludes(result, valueId)) {
      result.push(valueId);
    } else if (isIterable(value)) {
      const next = value;
      search(next, result);
    }
  }
  return result;
};
const getAllIcons = components => {
  var _components$enhanced_;
  if (!components) {
    return null;
  }

  // icons from FE
  let icons = ['arrow', 'bookmark', 'check_circle', 'chevron', 'help', 'play', 'close', 'search', 'sharegallery', 'response', 'price_info', 'verified_small', 'meli_coins', 'meli_usd', 'file'];
  if (
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  components.gallery && components.gallery.videos && components.gallery.videos.length > 0) {
    icons.push('youtube_video');
  }

  // reviews icons
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  if (components.header && components.header.reviews || components.reviews) {
    icons = [...icons, 'dislike', 'dislike_full', 'like', 'like_full'];
  }

  /* *
   * icons that may appear after any interaction
   * */
  // buybox icons
  icons = [...icons, 'truck', 'fast_truck', 'schedulable_truck', 'seller_agreement', 'pick_up', 'cbt', 'poly_full', 'full_icon', 'location', 'facing', 'warning', 'unit_floor', 'office', 'meliplus_icon', 'tooltip', 'rounded_truck_medium', 'calendar', 'question_ai', 'flag', 'cbt_summary', 'icon_package', 'location_rounded', 'protected_buy_rounded', 'coupon'];
  // tooltip icons
  icons = [...icons, 'rounded_truck_medium_alt', 'loyalty_trophy_rounded', 'loyalty_plus_rounded', 'cart', 'express'];
  // benefits & box icons
  icons = [...icons, 'warranty', 'returns', 'protected_buy', 'loyalty_points', 'meliplus_loyalty_points', 'card', 'withdrawal_right', 'smartphone'];

  // @recommendations-fe/icons
  icons = [...icons, 'recommendations-cpg_card', 'recommendations-supermarket_fs_icon', 'poly_compats', 'poly_coupon', 'poly_star', 'poly_star_fill', 'poly_star_half', 'melichoice_logo', 'buflo_icon_info', 'icon_meli', 'ic_online_visit_request'];

  // kits icons
  icons = [...icons, 'poly_size_chart'];

  // enhanced-content icons
  const hasEnhancedVideos = (_components$enhanced_ = components.enhanced_content) === null || _components$enhanced_ === void 0 || (_components$enhanced_ = _components$enhanced_.media_content) === null || _components$enhanced_ === void 0 ? void 0 : _components$enhanced_.some(elem => elem.type.toLowerCase() === 'video');
  if (hasEnhancedVideos) {
    icons = [...icons, 'play_button', 'pause', 'backwards', 'forwards', 'mute', 'unmute', 'fullscreen', 'normalscreen', 'retry', 'central_play', 'go_back'];
  }

  // icons from BE
  const allIcons = search(components, icons);
  return allIcons;
};

/* see https://reactjs.org/docs/higher-order-components.html#convention-wrap-the-display-name-for-easy-debugging */
const getDisplayName = Component => Component.displayName || Component.name || 'Component';
const MLA = 'MLA';
const MLB$2 = 'MLB';
const MLM = 'MLM';
const messagesErrorScreen = siteId => {
  switch (siteId) {
    case MLA:
      return {
        title: '¡Ups! hubo un error',
        description: 'Intentalo más tarde'
      };
    case MLB$2:
      return {
        title: 'Ops! Ocorreu um erro',
        description: 'Tente novamente mais tarde'
      };
    case MLM:
      return {
        title: '¡Ups! hubo un error',
        description: 'Inténtalo más tarde'
      };
    default:
      return {
        title: '¡Ups! hubo un error',
        description: 'Inténtalo más tarde'
      };
  }
};
const messagesNotFound = siteId => {
  switch (siteId) {
    case MLA:
      return {
        title: 'Parece que esta página no existe',
        text: 'Volver atrás'
      };
    case MLB$2:
      return {
        title: 'Parece que esta página não existe',
        text: 'Voltar atrás'
      };
    case MLM:
      return {
        title: 'Parece que esta página no existe',
        text: 'Volver atrás'
      };
    default:
      return {
        title: 'Parece que esta página no existe',
        text: 'Volver atrás'
      };
  }
};
const MLB$1 = 'MLB';
const getParentMessageRedirectFrom = siteId => {
  switch (siteId) {
    case MLB$1:
      return {
        label: 'Você está vendo um novo anúncio do mesmo vendedor, pois o produto que queria visitar finalizou.',
        link: 'Ver produto finalizado.'
      };
    default:
      return {
        label: 'Estás viendo una nueva publicación del mismo vendedor, el artículo que querías visitar finalizó.',
        link: 'Ver artículo finalizado.'
      };
  }
};
const getSimilarMessageRedirectFrom = siteId => {
  switch (siteId) {
    case MLB$1:
      return {
        label: 'Você está vendo um novo anúncio, pois o produto que queria visitar finalizou.'
      };
    default:
      return {
        label: 'Estás viendo una nueva publicación, el artículo que querías visitar finalizó.'
      };
  }
};
const getSimilarLinkMessageRedirectFrom = siteId => {
  switch (siteId) {
    case MLB$1:
      return {
        label: 'Você está vendo um novo anúncio, pois o produto que queria visitar finalizou.',
        link: 'Ver produto finalizado.'
      };
    default:
      return {
        label: 'Estás viendo una nueva publicación, el artículo que querías visitar finalizó.',
        link: 'Ver artículo finalizado.'
      };
  }
};
const MLB = 'MLB';
const messageSeeMore = siteId => {
  switch (siteId) {
    case MLB:
      return 'Ver mais';
    default:
      return 'Ver más';
  }
};
const getTestURLScopeParams = url => {
  const newUrl = new URL(url);
  const urlSearchParams = newUrl.searchParams;
  let params = null;
  urlSearchParams.forEach((paramValue, paramKey) => {
    if (paramValue.includes('test')) {
      params = params ? `${params}&${paramKey}=${paramValue}` : `${paramKey}=${paramValue}`;
      params.toString();
    }
  });
  return params;
};
const getTestEnvScopeParams = url => {
  const newUrl = new URL(url);
  const params = new URLSearchParams(newUrl.search);
  const scope = params.get('compats.env');
  if (scope) {
    return `env=${scope}`;
  }
  return '';
};
const getTestObjectScopeParamsFromUrl = url => {
  const newObject = {};
  const newUrl = new URL(url);
  const urlSearchParams = newUrl.searchParams;
  urlSearchParams.forEach((paramValue, paramKey) => {
    if (paramValue.includes('test')) {
      newObject[`${paramKey}`] = paramValue;
    }
  });
  return newObject;
};
const hyphenatedbem = text => text ? text.replace(/_/g, '-') : null;
const isSupported = async () => new Promise((resolve, reject) => {
  const test = '__use_a_unique_key_to_not_involuntary_replace_data__';
  try {
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    resolve();
  } catch (e) {
    reject(e);
  }
});
const isSupportedByDefault = () => {
  try {
    const test = '__use_a_unique_key_to_not_involuntary_replace_data__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};
const {
  DESKTOP_MAX_CHARS
} = constants;
const {
  MOBILE_MAX_CHARS
} = constants;
const {
  MOBILE_QADB_MAX_CHARS
} = constants;
const maxCharsDevice = (deviceType, type) => {
  if (deviceType === 'mobile' && type === 'qadb') {
    return MOBILE_QADB_MAX_CHARS;
  }
  if (deviceType === 'mobile') {
    return MOBILE_MAX_CHARS;
  }
  return DESKTOP_MAX_CHARS;
};

/* eslint-disable @typescript-eslint/naming-convention */
const dispatchSellerPixelsInfo = (trackingMediator, trackingData) => {
  const {
    item_id,
    variation_id,
    price,
    title,
    currency,
    brand,
    name
  } = trackingData;
  trackingMediator('dispatch', 'page:vip', {
    item_id,
    variation_id,
    price,
    title,
    brand,
    name,
    currency
  });
};
const dispatchAddToCardShopsInfo = (trackingMediator, trackingData) => {
  const {
    item_id,
    variation_id,
    price,
    title,
    currency,
    brand,
    name,
    quantity
  } = trackingData;
  trackingMediator('dispatch', 'item:info', {
    item_id,
    variation_id,
    price,
    title,
    brand,
    name,
    quantity,
    currency
  });
};
const getFormattedTrackingData = (trackingData, variationId) => {
  const {
    currency,
    price,
    title,
    item_id,
    brand,
    name,
    quantity
  } = trackingData;
  return {
    currency,
    price,
    title,
    item_id,
    variation_id: variationId,
    brand,
    quantity,
    name
  };
};
const {
  D2ID_REGEX,
  mshops
} = constants;
const isMshops = req => {
  var _req$body, _req$query, _req$platform;
  const channel = (req === null || req === void 0 || (_req$body = req.body) === null || _req$body === void 0 ? void 0 : _req$body.channel) || (req === null || req === void 0 || (_req$query = req.query) === null || _req$query === void 0 ? void 0 : _req$query.channel);
  return (req === null || req === void 0 || (_req$platform = req.platform) === null || _req$platform === void 0 ? void 0 : _req$platform.id) === mshops.SITE || channel === mshops.CHANNEL;
};
const isBuyboxShown = components => {
  const existsInTheComponents = Boolean(components.buy_box);
  const isShown = existsInTheComponents ? components.buy_box.state !== 'HIDDEN' : false;
  return isShown;
};
const getShopFeatures = req => {
  var _req$platform2;
  return req === null || req === void 0 || (_req$platform2 = req.platform) === null || _req$platform2 === void 0 || (_req$platform2 = _req$platform2.shop) === null || _req$platform2 === void 0 ? void 0 : _req$platform2.features;
};
const getShopsHeaders = req => {
  var _getShopFeatures;
  return {
    'X-SHOPS-FEATURES': ((_getShopFeatures = getShopFeatures(req)) === null || _getShopFeatures === void 0 ? void 0 : _getShopFeatures.length) > 0 ? getShopFeatures(req).join(',') : 'NO_FEATURES'
  };
};
const isJSON = str => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

/**
 * isValidProperty
 * @description validate if the property value is valid or have malicious code
 * @param {string} key
 * @param {string} value
 * @returns {boolean}
 */
const isValidProperty = (key, value) => {
  if (key === 'x-d2id') {
    return D2ID_REGEX.test(value);
  }
  if (key === 'x-push-tooltip-config') {
    return isJSON(value);
  }
  return true;
};

/**
 * sanitizeMshopsHeaders
 * @description sanitize valid mshops headers
 * @param {object} headers
 * @returns {object}
 */
const sanitizeMshopsHeaders = headers => {
  const localHeaders = _objectSpread({}, headers);
  const mshopsValidHeadersArray = mshops.VERIFY_HEADERS;
  mshopsValidHeadersArray.forEach(key => {
    if (localHeaders[`${key}`]) {
      const value = localHeaders[`${key}`];
      if (value && !isValidProperty(key, value)) {
        delete localHeaders[`${key}`];
      }
    }
  });
  return localHeaders;
};
const messageFormatter = data => {
  if (!isEmpty(data)) {
    return _objectSpread(_objectSpread({}, get(data, '[0]', null)), {}, {
      text: get(data, '[0].display_message.text', null),
      type: get(data, '[0].success', false) ? 'success' : 'error'
    });
  }
  return null;
};
const PriceFontSize = {
  DEFAULT: 36,
  MOBILE_DEFAULT: 32,
  ORIGINAL_DEFAULT: 16,
  OTHER_PRODUCT: 14,
  OTHER_SELLERS: {
    ITEM: 20,
    LINK: 14,
    ORIGINAL: 14
  },
  PRICE_NOT_SELECTED: {
    LARGE: 20
  },
  PRODUCT_SUBTITLE: {
    XXSMALL: 12,
    XSMALL: 14,
    SMALL: 16,
    MEDIUM: 18
  },
  MEDIA_SUBTITLE: 16,
  MEDIA_SHIPPING: 14,
  MOBILE_MEDIA_SHIPPING: 12,
  PRICE_RESULT: 20,
  ORIGINAL_PRICE_RESULT: 12,
  PAYMENT_ICON: {
    SMALL: 16
  },
  VARIATION_MODAL: 16,
  VARUATION_BOX: 12
};
const DiscountFontSize = {
  DEFAULT: 18,
  OTHER_SELLERS: {
    ITEM: 14
  },
  PRICE_RESULT: 14,
  MOBILE_PRICE_RESULT: 12
};
const entries = obj => {
  const ownProps = Object.keys(obj);
  let i = ownProps.length;
  const resArray = new Array(i); // preallocate the Array

  // eslint-disable-next-line no-plusplus
  while (i--) {
    resArray[i] = [ownProps[i], obj[ownProps[i]]];
  }
  return resArray;
};

/* eslint-disable complexity */
const onIframeModalResize = function (typeName, height, containerSelector, headerSelector, width, deviceType) {
  let extra = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : 10;
  let ratio = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : 0.7;
  return (type, data, elem) => {
    const modalTypes = ['coupons_height', 'vip:modal-content:loaded', 'nav:cp:updateSize', 'vip:shipping-calculator:updateSize', 'cart-congrats-height', 'vpp:iframe-modal:resize', 'vpp:on-demand-iframe:resize'];
    if (type === typeName && (data[height] || data.heightPercentage) || type === 'nav:cp:loaded' && data.deviceType === 'mobile' || arrayIncludes(modalTypes, type)) {
      const isNotMobile = data.deviceType !== 'mobile' && deviceType !== 'mobile';
      const messageHeight = parseInt(data[height], 10);
      const messageWidth = parseInt(data[width], 10);
      const container = document.querySelector(containerSelector);
      const containerHeader = headerSelector ? document.querySelector(`${headerSelector}`) : null;
      if (data.heightPercentage) {
        elem.style.height = `${window.innerHeight * (data.heightPercentage / 100)}px`;
      } else {
        elem.style.height = extra > 10 ? `${messageHeight + extra}px` : `${messageHeight}px`;
      }
      if (type === 'nav:cp:updateSize' && isNotMobile) {
        elem.scrolling = 'yes';
      } else if (type === 'vip:shipping-calculator:updateSize' && isNotMobile) {
        elem.scrolling = 'no';
      }
      if (messageWidth) {
        elem.style.width = `${messageWidth}px`;
        container.style.width = `${messageWidth}px`;
      }
      if (isNotMobile) {
        if (containerHeader) {
          container.style.height = `${messageHeight + containerHeader.clientHeight}px`;
        } else {
          container.style.height = `${messageHeight + 5}px`;
        }
        let headerDiff = 0;
        if (container) {
          const viewportHeight = window.innerHeight;
          if (headerSelector) {
            const header = container.querySelector(headerSelector);
            headerDiff = header ? header.clientHeight : 0;
          }
          const availableHeight = viewportHeight * ratio - headerDiff;
          if (messageHeight < availableHeight) {
            container.style.height = `${messageHeight + headerDiff + extra}px`;
          }
        }
      }
    }
  };
};
const PUSH_NOTIFICATION_BOOKMARK_MODAL = 'push_notification_bookmark_modal';
const PUSH_NOTIFICATION_QUESTION_MODAL = 'push_notification_question_modal';
const PUSH_NOTIFICATION_DECLINE_ACTION = 'DECLINE';
const hasGrantedNotifactions = cookies => get(cookies, '_ml_gcmid', null) !== null;
const hasBlockedNotifications = cookies => get(cookies, '_ml_clout', null) !== null;
const isPushNotificationsPermissionModal = id => arrayIncludes([PUSH_NOTIFICATION_BOOKMARK_MODAL, PUSH_NOTIFICATION_QUESTION_MODAL], id);
const renderWithSvgIcons = (res, next, svgIcons) => (error, output) => {
  // eslint-disable-next-line no-negated-condition
  if (!error) {
    res.send(output.replace(/<body\b[^>]*>/, match => `${match}${svgIcons}`));
  } else {
    const err = new Error();
    err.status = error.status || 500;
    err.message = error.message || 'Error occurred during the rendering phase';
    err.stack = error.stack || err.stack || 'Empty stack';
    next(err);
  }
};
const sanitizedData = messageData => {
  if (typeof messageData === 'object' && messageData !== null) {
    const sanitizedObject = {};
    Object.keys(messageData).forEach(key => {
      sanitizedObject[key] = DOMPurify.sanitize(messageData[key]);
    });
    return sanitizedObject;
  }
  return DOMPurify.sanitize(messageData);
};
const categoryFormatter = data => {
  if (data && !isEmpty(data)) {
    return {
      id: data.id,
      permalink: data.target,
      name: data.description,
      icon: data.icon,
      defaultChecked: data.checked
    };
  }
  return null;
};
const categoriesFormatter = searchFilters => {
  if (searchFilters && !isEmpty(searchFilters)) {
    return searchFilters.map(searchFilter => ({
      id: get(searchFilter, 'id', null),
      name: get(searchFilter, 'description', null),
      logo: get(searchFilter, 'logo', null),
      icon: get(searchFilter, 'icon', null),
      permalink: get(searchFilter, 'target', null),
      default_checked: get(searchFilter, 'checked', false),
      is_place_holder_value: get(searchFilter, 'is_place_holder_value', false)
    }));
  }
  return null;
};
const ONE_SECOND = 1000;
const ONE_MINUTE = 60 * ONE_SECOND;
const ONE_HOUR = 60 * ONE_MINUTE;
const PAD = '00';
const MORE_THAN_ONE_HOUR = 'MORE_THAN_ONE_HOUR';
const LESS_THAN_ONE_HOUR = 'LESS_THAN_ONE_HOUR';
const LESS_THAN_ONE_MINUTE = 'LESS_THAN_ONE_MINUTE';
const TIME_OUT = 'TIME_OUT';
const findLabel = function (timeLabel) {
  let labels = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  return labels.find(_ref => {
    let {
      id
    } = _ref;
    return id === timeLabel;
  });
};
const getTimerLabel = (time, labels) => {
  if (time >= ONE_HOUR) {
    return findLabel(MORE_THAN_ONE_HOUR, labels);
  }
  if (time >= ONE_MINUTE) {
    return findLabel(LESS_THAN_ONE_HOUR, labels);
  }
  if (time >= ONE_SECOND) {
    return findLabel(LESS_THAN_ONE_MINUTE, labels);
  }
  return findLabel(TIME_OUT, labels);
};
const formatTextLabel = (text, values) => {
  let result = text;
  entries(values).forEach(_ref2 => {
    let [key, value] = _ref2;
    result = result.replace(`{${key}}`, value);
  });
  return result.split(/{\d}/g);
};
const padNumber = function (number) {
  let pad = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : PAD;
  return (pad + number).slice(-pad.length);
};
const getTimerText = (time, labels) => {
  const label = getTimerLabel(time, labels);
  const values = {
    h: padNumber(Math.floor(time / ONE_HOUR)),
    m: padNumber(Math.floor(time % ONE_HOUR / ONE_MINUTE)),
    s: padNumber(Math.floor(time % ONE_HOUR % ONE_MINUTE / ONE_SECOND))
  };
  return label ? formatTextLabel(label.text, values) : null;
};

/* global melidata */

/**
 * Page Tracker
 *
 * @param {String} path
 * @param {Object} dimensions
 * @param {Object} experiments
 * @param {Object} data
 *
 * @example
 * trackPage({
 *     path: '/cart/my_cart/',
 *     data: {
 *         dimension1: 'dimension1',
 *     },
 * });
 */

function businessFixer(business) {
  if (business === 'UNKNOWN') {
    return 'mercadoshops';
  }
  return business;
}
function trackPage$2() {
  let {
    path = null,
    experiments = null,
    event_data = {},
    useBeacon = false
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  // If melidata is not defined do nothing for now
  if (typeof melidata !== 'function') {
    return;
  }

  // If one of this parameters is empty do not track
  if (!path) {
    return;
  }
  const track = {};
  melidata('createViewTrack', track);
  melidata('withPath', path, track);
  if (event_data) {
    if (event_data.is_mshops) {
      var _track$application;
      const currentBusiness = track === null || track === void 0 || (_track$application = track.application) === null || _track$application === void 0 ? void 0 : _track$application.business;
      if (currentBusiness) {
        track.application.business = businessFixer(currentBusiness);
      }
    }
    melidata('withData', event_data, track);
  }
  if (experiments) {
    melidata('withExperiments', experiments, track);
  }
  if (useBeacon) {
    track.useBeacon = true;
  }
  melidata('sendTrack', track);
}

/**
 * Event Tracker
 *
 * @param {String} path
 * @param {String} data
 * @param {String} clean
 * @param {Boolean} useBeacon
 *
 * If you send clean = true the event is going to change to cleanAndSend
 *
 * @example
 * trackEvent({
 *     path: '/cart/my_cart/',
 *     data: {},
 * });
 */
function trackEvent$2() {
  let {
    path = null,
    event_data = null,
    experiments = null,
    useBeacon = false,
    type = 'event'
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  // If melidata is not defined do nothing for now
  if (typeof melidata !== 'function') {
    return;
  }
  if (!path) {
    return;
  }
  const data = {
    path
  };
  if (event_data) {
    data.event_data = event_data;
  }
  if (experiments) {
    data.experiments = experiments;
  }
  if (useBeacon) {
    data.useBeacon = true;
  }
  melidata('cleanAndSend', type, data);
}
function trackEventWithExperiments() {
  var _track$application2;
  let {
    path = null,
    event_data = null,
    experiments = null,
    useBeacon = false
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  // If melidata is not defined do nothing for now
  if (typeof melidata !== 'function') {
    return;
  }
  if (!path) {
    return;
  }
  const track = {};
  melidata('createEventTrack', track);

  // If the site_id could not be inferred, trackEvent() is called which uses the track that is already initialized
  // This fix is applied for special cases in mercadoshops contexts.
  if ((track === null || track === void 0 || (_track$application2 = track.application) === null || _track$application2 === void 0 ? void 0 : _track$application2.site_id) === 'UNKNOWN') {
    trackEvent$2({
      path,
      event_data,
      experiments,
      useBeacon
    });
    return;
  }
  melidata('withPath', path, track);
  if (event_data) {
    melidata('withData', event_data, track);
  }
  if (experiments) {
    melidata('withExperiments', experiments, track);
  }
  if (useBeacon) {
    track.useBeacon = true;
  }
  melidata('sendTrack', track);
}
const trackEventOnComponentPrint$1 = _ref => {
  let {
    path = null,
    event_data = null,
    useBeacon = false
  } = _ref;
  const meliTrack = {};
  melidata('createEventTrack', meliTrack);
  melidata('withPath', path, meliTrack);
  melidata('withData', _objectSpread({}, event_data), meliTrack);
  if (useBeacon) {
    meliTrack.useBeacon = true;
  }
  melidata('sendTrack', meliTrack, 'component_prints');
};
const meliData = {
  trackPage: trackPage$2,
  trackEvent: trackEvent$2,
  trackEventWithExperiments,
  trackEventOnComponentPrint: trackEventOnComponentPrint$1,
  businessFixer
};

/* global meli_ga  */

/**
 * @param string section
 * @param string page
 *
 * @returns string
 */
const formatTrackerPage = (section, page) => {
  let trackerPage = `/${section.toUpperCase()}/${page ? page.toUpperCase() : ''}`.replace(/\/{2,}/g, '/');
  if (!trackerPage.endsWith('/')) {
    trackerPage += '/';
  }
  return trackerPage;
};

/**
 * Track page
 *
 * @param section
 * @param page
 * @param dimensions
 * @param platform
 *
 * @example
 * trackPage({
 *     section: 'CHECKOUT',
 *     page: '/CARD_FORM/',
 *     dimensions: {
 *         dimension1: 'dimension1',
 *     },
 * });
 */
function trackPage$1() {
  let {
    section,
    page,
    platform,
    dimensions,
    custom_dimensions
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  // Check meli_ga is available, do nothing for now

  if (typeof meli_ga === 'undefined') {
    return;
  }

  // If this parameters is empty do not track
  if (!section) {
    return;
  }
  const trackerPage = formatTrackerPage(section, page);

  // Set default tracks
  if (platform) {
    meli_ga('set', 'platform', platform);
  }

  // meli_ga('set', 'section', section.toUpperCase());
  meli_ga('set', 'page', trackerPage);

  // Set dimensions track
  if (dimensions && Object.keys(dimensions).length > 0) {
    Object.keys(dimensions).forEach(key => {
      meli_ga('set', key, dimensions[key]);
    });
  }

  // Set dimensions track
  if (custom_dimensions && Object.keys(custom_dimensions).length > 0) {
    Object.keys(custom_dimensions).forEach(key => {
      meli_ga('set', `dimension${key}`, custom_dimensions[key]);
    });
  }

  // Send Tracks
  meli_ga('send', 'pageview');
}

/**
 * Track event
 *
 * @param {String} category
 * @param {String} action
 * @param {String} label
 * @param {Object.<string, string>} custom_dimensions
 *
 * @example
 * trackEvent({
 *     category: 'CHECKOUT',
 *     action: 'VALID_CREDIT_CARD',
 *     label: 'CUSTOM_LABEL',
 *     custom_dimensions: { dimension133: example }
 * });
 */
function trackEvent$1() {
  let {
    category = null,
    action = null,
    label = null,
    custom_dimensions = null
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  // Check meli_ga is available, do nothing for now

  if (typeof meli_ga === 'undefined') {
    return;
  }

  // If one of this parameters is empty do not track
  if (!category || !action) {
    return;
  }

  // Send event track
  let parsedCustomDimensions = {};
  if (custom_dimensions) {
    parsedCustomDimensions = Object.keys(custom_dimensions).reduce((acc, key) => {
      if (String(key).includes('dimension')) {
        acc[key] = custom_dimensions[key];
      } else {
        acc[`dimension${key}`] = custom_dimensions[key];
      }
      return acc;
    }, {});
  }
  parsedCustomDimensions.useBeacon = true;

  // Send event track
  meli_ga('send', 'event', category, action, label, parsedCustomDimensions);
}
const analytics = {
  trackPage: trackPage$1,
  trackEvent: trackEvent$1
};
function trackGtmEvent(gtmEventData) {
  if (global.window && typeof global.window.dataLayer !== 'undefined' && gtmEventData) {
    global.window.dataLayer.push(gtmEventData);
  }
}
const canUseDOM = !!(typeof window !== 'undefined');
const isScheduleTaskSupported = () => canUseDOM && typeof window.scheduler !== 'undefined';

/**
 * @description: this function run the scheduler task
 * @param {function} callback: callback to run
 * @param {string} priority: priority to run. options ("user-blocking", "user-visible", "background".).
 */
const runSchedulerTask = (callback, priority) => {
  // Check that feature is supported
  if (isScheduleTaskSupported()) {
    window.scheduler.postTask(callback, {
      priority
    }).then(console.info) // Log resolved value
    .catch(console.error); // Log error or abort;
  } else {
    setTimeout(() => {
      callback();
    }, 0);
  }
};

/**
 * @description: this function run the scheduler task and returns a promise.
 * @param {function} callback: callback to run
 * @param {string} priority: priority to run. options ("user-blocking", "user-visible", "background".).
 * @returns {Promise} promise for task execution.
 */
const runSchedulerTaskPromise = async (callback, priority) => {
  // Check that feature is supported
  if (isScheduleTaskSupported()) {
    return window.scheduler.postTask(callback, {
      priority
    }).then(console.info) // Log resolved value
    .catch(console.error); // Log error or abort;
  }
  return new Promise(resolve => {
    setTimeout(() => {
      callback();
      resolve();
    }, 0);
  });
};
const trackPage = tracks => {
  runSchedulerTask(() => {
    if (tracks !== null && tracks !== void 0 && tracks.melidata_event) {
      meliData.trackPage(tracks.melidata_event);
    }
    if (tracks !== null && tracks !== void 0 && tracks.analytics_event) {
      analytics.trackPage(tracks.analytics_event);
    }
  }, 'background');
};
const trackEventFunction = tracks => () => {
  if (tracks !== null && tracks !== void 0 && tracks.melidata_event) {
    var _tracks$melidata_even;
    if (tracks !== null && tracks !== void 0 && (_tracks$melidata_even = tracks.melidata_event) !== null && _tracks$melidata_even !== void 0 && _tracks$melidata_even.experiments) {
      meliData.trackEventWithExperiments(tracks.melidata_event);
    } else {
      meliData.trackEvent(tracks.melidata_event);
    }
  }
  if (tracks !== null && tracks !== void 0 && tracks.analytics_event) {
    analytics.trackEvent(tracks.analytics_event);
  }
  if (tracks !== null && tracks !== void 0 && tracks.gtm_event) {
    trackGtmEvent(tracks.gtm_event);
  }
};
const trackEvent = tracks => runSchedulerTask(trackEventFunction(tracks), 'background');
const trackEventPromise = async tracks => runSchedulerTaskPromise(trackEventFunction(tracks), 'background');
const trackEventSync = tracks => trackEventFunction(tracks)();
const trackQuantityEvent = (track, q) => {
  runSchedulerTask(() => {
    var _track$melidata_event;
    const event = track === null || track === void 0 || (_track$melidata_event = track.melidata_event) === null || _track$melidata_event === void 0 ? void 0 : _track$melidata_event.event_data;
    if (event) {
      event.quantity = q;
      trackEvent(track);
    }
  }, 'background');
};
const trackEventWithCustomField = (key, track, value) => {
  runSchedulerTask(() => {
    var _track$melidata_event2;
    if (track !== null && track !== void 0 && (_track$melidata_event2 = track.melidata_event) !== null && _track$melidata_event2 !== void 0 && _track$melidata_event2.event_data) {
      track.melidata_event.event_data[key] = value;
      trackEvent(track);
    }
  }, 'background');
};
const trackEventWithCustomFields = (track, newFields) => {
  runSchedulerTask(() => {
    if (track !== null && track !== void 0 && track.melidata_event) {
      if (!track.melidata_event.event_data) {
        track.melidata_event.event_data = {};
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of Object.entries(newFields)) {
        track.melidata_event.event_data[key] = value;
      }
      trackEvent(track);
    }
  }, 'background');
};
if (global.window && global.window !== undefined) {
  global.window.Tracking = {
    trackPage,
    trackEvent
  };
}
const {
  trackEventOnComponentPrint
} = meliData;

/* eslint-disable security/detect-non-literal-regexp */
const {
  itemOrProductIdByGroupRegex
} = constants;
const {
  vipItemIdRegex
} = constants;
const itemIdToVipFormat = baseItemId => {
  let result = null;
  const itemRegex = new RegExp(itemOrProductIdByGroupRegex);
  const regexResult = itemRegex.exec(baseItemId);
  if (regexResult && regexResult.length > 1) {
    result = `${regexResult[1]}-${regexResult[2]}`;
  }
  return result;
};
const vipFormatToItemId = baseItemId => {
  let result = null;
  const itemRegex = new RegExp(vipItemIdRegex);
  const regexResult = itemRegex.exec(baseItemId);
  if (regexResult && regexResult.length > 1) {
    result = `${regexResult[1]}${regexResult[2]}`;
  }
  return result;
};
const transformActionTarget = (actionElem, params) => {
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  if (!actionElem || !actionElem.target) {
    return;
  }
  try {
    const urlObj = new URL(actionElem.target);
    entries(params).forEach(_ref => {
      let [key, value] = _ref;
      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
      if (urlObj && urlObj.searchParams) {
        urlObj.searchParams.set(key, value);
      }
    });
    actionElem.target = urlObj.href;
  } catch (e) {
    // invalid URL
  }
};

// eslint-disable-next-line react/display-name
const withMappedProps = Component => mapper => props => {
  // eslint-disable-next-line react/no-unstable-nested-components
  const WithMappedProps = newProps => /*#__PURE__*/React.createElement(Component, mapper(newProps));
  WithMappedProps.displayName = `WithMappedProps(${getDisplayName(WithMappedProps)})`;
  return /*#__PURE__*/React.createElement(WithMappedProps, props);
};
const base = {
  disabled: false
};

// Punto para apagar el partial Hydration en donde consuman esta utils
const FEATURE_ENABLED = true;
const whenVisible = () => _objectSpread(_objectSpread({}, base), {
  whenVisible: true
});
const whenIdle = () => _objectSpread(_objectSpread({}, base), {
  whenIdle: true
});
const ssrOnly = () => _objectSpread(_objectSpread({}, base), {
  ssrOnly: true
});
const disabled = () => _objectSpread(_objectSpread({}, base), {
  disabled: true
});
const targetHostnameValidator = _ref => {
  let {
    target,
    currentLocation = {}
  } = _ref;
  const ML_REGEX = /^.+\.mercadoli(b|v)re\.(cl|com\.(ar|bo|br|co|cr|cu|do|ec|gt|mx|ni|hn|pa|pe|py|sv|ve|uy)|com)$/;
  const MP_REGEX = /^.+\.mercadopago\.(cl|com\.(ar|bo|br|co|cr|cu|do|ec|gt|mx|ni|hn|pa|pe|py|sv|ve|uy)|com)$/;
  const TUCARRO_REGEX = /^.+\.tucarro\.(cl|com\.(ar|bo|br|co|cr|cu|do|ec|gt|mx|ni|hn|pa|pe|py|sv|ve|uy)|com)$/;
  const PORTAL_REGEX = /^.+\.portalinmobiliario\.(cl|com\.(ar|bo|br|co|cr|cu|do|ec|gt|mx|ni|hn|pa|pe|py|sv|ve|uy)|com)$/;
  try {
    const targetUrl = new URL(target);
    const targetHostname = targetUrl.hostname;
    let currentLocationHostname;
    if (typeof currentLocation === 'string') {
      const currentLocationUrl = new URL(currentLocation);
      currentLocationHostname = currentLocationUrl.hostname;
    } else if (typeof currentLocation === 'object' && currentLocation.hostname) {
      currentLocationHostname = currentLocation.hostname;
    }
    const isMeliDomain = ML_REGEX.test(targetHostname) || MP_REGEX.test(targetHostname);
    const isThirdParty = TUCARRO_REGEX.test(targetHostname) || PORTAL_REGEX.test(targetHostname);
    const isSameHostname = targetHostname === currentLocationHostname;
    return isMeliDomain || isThirdParty || isSameHostname;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('An error occurred validating the target', error);
    return false;
  }
};
const idToUrl = (id, template) => template.replace('{id}', id);
const updateComponentPositionWhenSticky = name => {
  const stickyWrapper = document.querySelector(`.ui-pdp--sticky-wrapper-${name}`);
  if (stickyWrapper) {
    stickyWrapper.style.top = '10px';
    stickyWrapper.style.marginTop = '0px';
    stickyWrapper.style.transform = 'translateY(0px)';
  }
};
var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};
function createCommonjsModule(fn, basedir, module) {
  return module = {
    path: basedir,
    exports: {},
    require: function (path, base) {
      return commonjsRequire(path, base === undefined || base === null ? module.path : base);
    }
  }, fn(module, module.exports), module.exports;
}
function commonjsRequire() {
  throw new Error('Dynamic requires are not currently supported by @rollup/plugin-commonjs');
}
var validate = createCommonjsModule(function (module, exports) {
  /*!
   * validate.js 0.13.1
   *
   * (c) 2013-2019 Nicklas Ansman, 2013 Wrapp
   * Validate.js may be freely distributed under the MIT license.
   * For all details and documentation:
   * http://validatejs.org/
   */

  (function (exports, module, define) {
    // The main function that calls the validators specified by the constraints.
    // The options are the following:
    //   - format (string) - An option that controls how the returned value is formatted
    //     * flat - Returns a flat array of just the error messages
    //     * grouped - Returns the messages grouped by attribute (default)
    //     * detailed - Returns an array of the raw validation data
    //   - fullMessages (boolean) - If `true` (default) the attribute name is prepended to the error.
    //
    // Please note that the options are also passed to each validator.
    var validate = function (attributes, constraints, options) {
      options = v.extend({}, v.options, options);
      var results = v.runValidations(attributes, constraints, options);
      if (results.some(function (r) {
        return v.isPromise(r.error);
      })) {
        throw new Error("Use validate.async if you want support for promises");
      }
      return validate.processValidationResults(results, options);
    };
    var v = validate;

    // Copies over attributes from one or more sources to a single destination.
    // Very much similar to underscore's extend.
    // The first argument is the target object and the remaining arguments will be
    // used as sources.
    v.extend = function (obj) {
      [].slice.call(arguments, 1).forEach(function (source) {
        for (var attr in source) {
          obj[attr] = source[attr];
        }
      });
      return obj;
    };
    v.extend(validate, {
      // This is the version of the library as a semver.
      // The toString function will allow it to be coerced into a string
      version: {
        major: 0,
        minor: 13,
        patch: 1,
        metadata: null,
        toString: function () {
          var version = v.format("%{major}.%{minor}.%{patch}", v.version);
          if (!v.isEmpty(v.version.metadata)) {
            version += "+" + v.version.metadata;
          }
          return version;
        }
      },
      // Below is the dependencies that are used in validate.js

      // The constructor of the Promise implementation.
      // If you are using Q.js, RSVP or any other A+ compatible implementation
      // override this attribute to be the constructor of that promise.
      // Since jQuery promises aren't A+ compatible they won't work.
      Promise: typeof Promise !== "undefined" ? Promise : /* istanbul ignore next */null,
      EMPTY_STRING_REGEXP: /^\s*$/,
      // Runs the validators specified by the constraints object.
      // Will return an array of the format:
      //     [{attribute: "<attribute name>", error: "<validation result>"}, ...]
      runValidations: function (attributes, constraints, options) {
        var results = [],
          attr,
          validatorName,
          value,
          validators,
          validator,
          validatorOptions,
          error;
        if (v.isDomElement(attributes) || v.isJqueryElement(attributes)) {
          attributes = v.collectFormValues(attributes);
        }

        // Loops through each constraints, finds the correct validator and run it.
        for (attr in constraints) {
          value = v.getDeepObjectValue(attributes, attr);
          // This allows the constraints for an attribute to be a function.
          // The function will be called with the value, attribute name, the complete dict of
          // attributes as well as the options and constraints passed in.
          // This is useful when you want to have different
          // validations depending on the attribute value.
          validators = v.result(constraints[attr], value, attributes, attr, options, constraints);
          for (validatorName in validators) {
            validator = v.validators[validatorName];
            if (!validator) {
              error = v.format("Unknown validator %{name}", {
                name: validatorName
              });
              throw new Error(error);
            }
            validatorOptions = validators[validatorName];
            // This allows the options to be a function. The function will be
            // called with the value, attribute name, the complete dict of
            // attributes as well as the options and constraints passed in.
            // This is useful when you want to have different
            // validations depending on the attribute value.
            validatorOptions = v.result(validatorOptions, value, attributes, attr, options, constraints);
            if (!validatorOptions) {
              continue;
            }
            results.push({
              attribute: attr,
              value: value,
              validator: validatorName,
              globalOptions: options,
              attributes: attributes,
              options: validatorOptions,
              error: validator.call(validator, value, validatorOptions, attr, attributes, options)
            });
          }
        }
        return results;
      },
      // Takes the output from runValidations and converts it to the correct
      // output format.
      processValidationResults: function (errors, options) {
        errors = v.pruneEmptyErrors(errors, options);
        errors = v.expandMultipleErrors(errors, options);
        errors = v.convertErrorMessages(errors, options);
        var format = options.format || "grouped";
        if (typeof v.formatters[format] === 'function') {
          errors = v.formatters[format](errors);
        } else {
          throw new Error(v.format("Unknown format %{format}", options));
        }
        return v.isEmpty(errors) ? undefined : errors;
      },
      // Runs the validations with support for promises.
      // This function will return a promise that is settled when all the
      // validation promises have been completed.
      // It can be called even if no validations returned a promise.
      async: function (attributes, constraints, options) {
        options = v.extend({}, v.async.options, options);
        var WrapErrors = options.wrapErrors || function (errors) {
          return errors;
        };

        // Removes unknown attributes
        if (options.cleanAttributes !== false) {
          attributes = v.cleanAttributes(attributes, constraints);
        }
        var results = v.runValidations(attributes, constraints, options);
        return new v.Promise(function (resolve, reject) {
          v.waitForResults(results).then(function () {
            var errors = v.processValidationResults(results, options);
            if (errors) {
              reject(new WrapErrors(errors, options, attributes, constraints));
            } else {
              resolve(attributes);
            }
          }, function (err) {
            reject(err);
          });
        });
      },
      single: function (value, constraints, options) {
        options = v.extend({}, v.single.options, options, {
          format: "flat",
          fullMessages: false
        });
        return v({
          single: value
        }, {
          single: constraints
        }, options);
      },
      // Returns a promise that is resolved when all promises in the results array
      // are settled. The promise returned from this function is always resolved,
      // never rejected.
      // This function modifies the input argument, it replaces the promises
      // with the value returned from the promise.
      waitForResults: function (results) {
        // Create a sequence of all the results starting with a resolved promise.
        return results.reduce(function (memo, result) {
          // If this result isn't a promise skip it in the sequence.
          if (!v.isPromise(result.error)) {
            return memo;
          }
          return memo.then(function () {
            return result.error.then(function (error) {
              result.error = error || null;
            });
          });
        }, new v.Promise(function (r) {
          r();
        })); // A resolved promise
      },
      // If the given argument is a call: function the and: function return the value
      // otherwise just return the value. Additional arguments will be passed as
      // arguments to the function.
      // Example:
      // ```
      // result('foo') // 'foo'
      // result(Math.max, 1, 2) // 2
      // ```
      result: function (value) {
        var args = [].slice.call(arguments, 1);
        if (typeof value === 'function') {
          value = value.apply(null, args);
        }
        return value;
      },
      // Checks if the value is a number. This function does not consider NaN a
      // number like many other `isNumber` functions do.
      isNumber: function (value) {
        return typeof value === 'number' && !isNaN(value);
      },
      // Returns false if the object is not a function
      isFunction: function (value) {
        return typeof value === 'function';
      },
      // A simple check to verify that the value is an integer. Uses `isNumber`
      // and a simple modulo check.
      isInteger: function (value) {
        return v.isNumber(value) && value % 1 === 0;
      },
      // Checks if the value is a boolean
      isBoolean: function (value) {
        return typeof value === 'boolean';
      },
      // Uses the `Object` function to check if the given argument is an object.
      isObject: function (obj) {
        return obj === Object(obj);
      },
      // Simply checks if the object is an instance of a date
      isDate: function (obj) {
        return obj instanceof Date;
      },
      // Returns false if the object is `null` of `undefined`
      isDefined: function (obj) {
        return obj !== null && obj !== undefined;
      },
      // Checks if the given argument is a promise. Anything with a `then`
      // function is considered a promise.
      isPromise: function (p) {
        return !!p && v.isFunction(p.then);
      },
      isJqueryElement: function (o) {
        return o && v.isString(o.jquery);
      },
      isDomElement: function (o) {
        if (!o) {
          return false;
        }
        if (!o.querySelectorAll || !o.querySelector) {
          return false;
        }
        if (v.isObject(document) && o === document) {
          return true;
        }

        // http://stackoverflow.com/a/384380/699304
        /* istanbul ignore else */
        if (typeof HTMLElement === "object") {
          return o instanceof HTMLElement;
        } else {
          return o && typeof o === "object" && o !== null && o.nodeType === 1 && typeof o.nodeName === "string";
        }
      },
      isEmpty: function (value) {
        var attr;

        // Null and undefined are empty
        if (!v.isDefined(value)) {
          return true;
        }

        // functions are non empty
        if (v.isFunction(value)) {
          return false;
        }

        // Whitespace only strings are empty
        if (v.isString(value)) {
          return v.EMPTY_STRING_REGEXP.test(value);
        }

        // For arrays we use the length property
        if (v.isArray(value)) {
          return value.length === 0;
        }

        // Dates have no attributes but aren't empty
        if (v.isDate(value)) {
          return false;
        }

        // If we find at least one property we consider it non empty
        if (v.isObject(value)) {
          for (attr in value) {
            return false;
          }
          return true;
        }
        return false;
      },
      // Formats the specified strings with the given values like so:
      // ```
      // format("Foo: %{foo}", {foo: "bar"}) // "Foo bar"
      // ```
      // If you want to write %{...} without having it replaced simply
      // prefix it with % like this `Foo: %%{foo}` and it will be returned
      // as `"Foo: %{foo}"`
      format: v.extend(function (str, vals) {
        if (!v.isString(str)) {
          return str;
        }
        return str.replace(v.format.FORMAT_REGEXP, function (m0, m1, m2) {
          if (m1 === '%') {
            return "%{" + m2 + "}";
          } else {
            return String(vals[m2]);
          }
        });
      }, {
        // Finds %{key} style patterns in the given string
        FORMAT_REGEXP: /(%?)%\{([^\}]+)\}/g
      }),
      // "Prettifies" the given string.
      // Prettifying means replacing [.\_-] with spaces as well as splitting
      // camel case words.
      prettify: function (str) {
        if (v.isNumber(str)) {
          // If there are more than 2 decimals round it to two
          if (str * 100 % 1 === 0) {
            return "" + str;
          } else {
            return parseFloat(Math.round(str * 100) / 100).toFixed(2);
          }
        }
        if (v.isArray(str)) {
          return str.map(function (s) {
            return v.prettify(s);
          }).join(", ");
        }
        if (v.isObject(str)) {
          if (!v.isDefined(str.toString)) {
            return JSON.stringify(str);
          }
          return str.toString();
        }

        // Ensure the string is actually a string
        str = "" + str;
        return str
        // Splits keys separated by periods
        .replace(/([^\s])\.([^\s])/g, '$1 $2')
        // Removes backslashes
        .replace(/\\+/g, '')
        // Replaces - and - with space
        .replace(/[_-]/g, ' ')
        // Splits camel cased words
        .replace(/([a-z])([A-Z])/g, function (m0, m1, m2) {
          return "" + m1 + " " + m2.toLowerCase();
        }).toLowerCase();
      },
      stringifyValue: function (value, options) {
        var prettify = options && options.prettify || v.prettify;
        return prettify(value);
      },
      isString: function (value) {
        return typeof value === 'string';
      },
      isArray: function (value) {
        return {}.toString.call(value) === '[object Array]';
      },
      // Checks if the object is a hash, which is equivalent to an object that
      // is neither an array nor a function.
      isHash: function (value) {
        return v.isObject(value) && !v.isArray(value) && !v.isFunction(value);
      },
      contains: function (obj, value) {
        if (!v.isDefined(obj)) {
          return false;
        }
        if (v.isArray(obj)) {
          return obj.indexOf(value) !== -1;
        }
        return value in obj;
      },
      unique: function (array) {
        if (!v.isArray(array)) {
          return array;
        }
        return array.filter(function (el, index, array) {
          return array.indexOf(el) == index;
        });
      },
      forEachKeyInKeypath: function (object, keypath, callback) {
        if (!v.isString(keypath)) {
          return undefined;
        }
        var key = "",
          i,
          escape = false;
        for (i = 0; i < keypath.length; ++i) {
          switch (keypath[i]) {
            case '.':
              if (escape) {
                escape = false;
                key += '.';
              } else {
                object = callback(object, key, false);
                key = "";
              }
              break;
            case '\\':
              if (escape) {
                escape = false;
                key += '\\';
              } else {
                escape = true;
              }
              break;
            default:
              escape = false;
              key += keypath[i];
              break;
          }
        }
        return callback(object, key, true);
      },
      getDeepObjectValue: function (obj, keypath) {
        if (!v.isObject(obj)) {
          return undefined;
        }
        return v.forEachKeyInKeypath(obj, keypath, function (obj, key) {
          if (v.isObject(obj)) {
            return obj[key];
          }
        });
      },
      // This returns an object with all the values of the form.
      // It uses the input name as key and the value as value
      // So for example this:
      // <input type="text" name="email" value="foo@bar.com" />
      // would return:
      // {email: "foo@bar.com"}
      collectFormValues: function (form, options) {
        var values = {},
          i,
          j,
          input,
          inputs,
          option,
          value;
        if (v.isJqueryElement(form)) {
          form = form[0];
        }
        if (!form) {
          return values;
        }
        options = options || {};
        inputs = form.querySelectorAll("input[name], textarea[name]");
        for (i = 0; i < inputs.length; ++i) {
          input = inputs.item(i);
          if (v.isDefined(input.getAttribute("data-ignored"))) {
            continue;
          }
          var name = input.name.replace(/\./g, "\\\\.");
          value = v.sanitizeFormValue(input.value, options);
          if (input.type === "number") {
            value = value ? +value : null;
          } else if (input.type === "checkbox") {
            if (input.attributes.value) {
              if (!input.checked) {
                value = values[name] || null;
              }
            } else {
              value = input.checked;
            }
          } else if (input.type === "radio") {
            if (!input.checked) {
              value = values[name] || null;
            }
          }
          values[name] = value;
        }
        inputs = form.querySelectorAll("select[name]");
        for (i = 0; i < inputs.length; ++i) {
          input = inputs.item(i);
          if (v.isDefined(input.getAttribute("data-ignored"))) {
            continue;
          }
          if (input.multiple) {
            value = [];
            for (j in input.options) {
              option = input.options[j];
              if (option && option.selected) {
                value.push(v.sanitizeFormValue(option.value, options));
              }
            }
          } else {
            var _val = typeof input.options[input.selectedIndex] !== 'undefined' ? input.options[input.selectedIndex].value : /* istanbul ignore next */'';
            value = v.sanitizeFormValue(_val, options);
          }
          values[input.name] = value;
        }
        return values;
      },
      sanitizeFormValue: function (value, options) {
        if (options.trim && v.isString(value)) {
          value = value.trim();
        }
        if (options.nullify !== false && value === "") {
          return null;
        }
        return value;
      },
      capitalize: function (str) {
        if (!v.isString(str)) {
          return str;
        }
        return str[0].toUpperCase() + str.slice(1);
      },
      // Remove all errors who's error attribute is empty (null or undefined)
      pruneEmptyErrors: function (errors) {
        return errors.filter(function (error) {
          return !v.isEmpty(error.error);
        });
      },
      // In
      // [{error: ["err1", "err2"], ...}]
      // Out
      // [{error: "err1", ...}, {error: "err2", ...}]
      //
      // All attributes in an error with multiple messages are duplicated
      // when expanding the errors.
      expandMultipleErrors: function (errors) {
        var ret = [];
        errors.forEach(function (error) {
          // Removes errors without a message
          if (v.isArray(error.error)) {
            error.error.forEach(function (msg) {
              ret.push(v.extend({}, error, {
                error: msg
              }));
            });
          } else {
            ret.push(error);
          }
        });
        return ret;
      },
      // Converts the error mesages by prepending the attribute name unless the
      // message is prefixed by ^
      convertErrorMessages: function (errors, options) {
        options = options || {};
        var ret = [],
          prettify = options.prettify || v.prettify;
        errors.forEach(function (errorInfo) {
          var error = v.result(errorInfo.error, errorInfo.value, errorInfo.attribute, errorInfo.options, errorInfo.attributes, errorInfo.globalOptions);
          if (!v.isString(error)) {
            ret.push(errorInfo);
            return;
          }
          if (error[0] === '^') {
            error = error.slice(1);
          } else if (options.fullMessages !== false) {
            error = v.capitalize(prettify(errorInfo.attribute)) + " " + error;
          }
          error = error.replace(/\\\^/g, "^");
          error = v.format(error, {
            value: v.stringifyValue(errorInfo.value, options)
          });
          ret.push(v.extend({}, errorInfo, {
            error: error
          }));
        });
        return ret;
      },
      // In:
      // [{attribute: "<attributeName>", ...}]
      // Out:
      // {"<attributeName>": [{attribute: "<attributeName>", ...}]}
      groupErrorsByAttribute: function (errors) {
        var ret = {};
        errors.forEach(function (error) {
          var list = ret[error.attribute];
          if (list) {
            list.push(error);
          } else {
            ret[error.attribute] = [error];
          }
        });
        return ret;
      },
      // In:
      // [{error: "<message 1>", ...}, {error: "<message 2>", ...}]
      // Out:
      // ["<message 1>", "<message 2>"]
      flattenErrorsToArray: function (errors) {
        return errors.map(function (error) {
          return error.error;
        }).filter(function (value, index, self) {
          return self.indexOf(value) === index;
        });
      },
      cleanAttributes: function (attributes, whitelist) {
        function whitelistCreator(obj, key, last) {
          if (v.isObject(obj[key])) {
            return obj[key];
          }
          return obj[key] = last ? true : {};
        }
        function buildObjectWhitelist(whitelist) {
          var ow = {},
            attr;
          for (attr in whitelist) {
            if (!whitelist[attr]) {
              continue;
            }
            v.forEachKeyInKeypath(ow, attr, whitelistCreator);
          }
          return ow;
        }
        function cleanRecursive(attributes, whitelist) {
          if (!v.isObject(attributes)) {
            return attributes;
          }
          var ret = v.extend({}, attributes),
            w,
            attribute;
          for (attribute in attributes) {
            w = whitelist[attribute];
            if (v.isObject(w)) {
              ret[attribute] = cleanRecursive(ret[attribute], w);
            } else if (!w) {
              delete ret[attribute];
            }
          }
          return ret;
        }
        if (!v.isObject(whitelist) || !v.isObject(attributes)) {
          return {};
        }
        whitelist = buildObjectWhitelist(whitelist);
        return cleanRecursive(attributes, whitelist);
      },
      exposeModule: function (validate, root, exports, module, define) {
        if (exports) {
          if (module && module.exports) {
            exports = module.exports = validate;
          }
          exports.validate = validate;
        } else {
          root.validate = validate;
          if (validate.isFunction(define) && define.amd) {
            define([], function () {
              return validate;
            });
          }
        }
      },
      warn: function (msg) {
        if (typeof console !== "undefined" && console.warn) {
          console.warn("[validate.js] " + msg);
        }
      },
      error: function (msg) {
        if (typeof console !== "undefined" && console.error) {
          console.error("[validate.js] " + msg);
        }
      }
    });
    validate.validators = {
      // Presence validates that the value isn't empty
      presence: function (value, options) {
        options = v.extend({}, this.options, options);
        if (options.allowEmpty !== false ? !v.isDefined(value) : v.isEmpty(value)) {
          return options.message || this.message || "can't be blank";
        }
      },
      length: function (value, options, attribute) {
        // Empty values are allowed
        if (!v.isDefined(value)) {
          return;
        }
        options = v.extend({}, this.options, options);
        var is = options.is,
          maximum = options.maximum,
          minimum = options.minimum,
          tokenizer = options.tokenizer || function (val) {
            return val;
          },
          err,
          errors = [];
        value = tokenizer(value);
        var length = value.length;
        if (!v.isNumber(length)) {
          return options.message || this.notValid || "has an incorrect length";
        }

        // Is checks
        if (v.isNumber(is) && length !== is) {
          err = options.wrongLength || this.wrongLength || "is the wrong length (should be %{count} characters)";
          errors.push(v.format(err, {
            count: is
          }));
        }
        if (v.isNumber(minimum) && length < minimum) {
          err = options.tooShort || this.tooShort || "is too short (minimum is %{count} characters)";
          errors.push(v.format(err, {
            count: minimum
          }));
        }
        if (v.isNumber(maximum) && length > maximum) {
          err = options.tooLong || this.tooLong || "is too long (maximum is %{count} characters)";
          errors.push(v.format(err, {
            count: maximum
          }));
        }
        if (errors.length > 0) {
          return options.message || errors;
        }
      },
      numericality: function (value, options, attribute, attributes, globalOptions) {
        // Empty values are fine
        if (!v.isDefined(value)) {
          return;
        }
        options = v.extend({}, this.options, options);
        var errors = [],
          name,
          count,
          checks = {
            greaterThan: function (v, c) {
              return v > c;
            },
            greaterThanOrEqualTo: function (v, c) {
              return v >= c;
            },
            equalTo: function (v, c) {
              return v === c;
            },
            lessThan: function (v, c) {
              return v < c;
            },
            lessThanOrEqualTo: function (v, c) {
              return v <= c;
            },
            divisibleBy: function (v, c) {
              return v % c === 0;
            }
          },
          prettify = options.prettify || globalOptions && globalOptions.prettify || v.prettify;

        // Strict will check that it is a valid looking number
        if (v.isString(value) && options.strict) {
          var pattern = "^-?(0|[1-9]\\d*)";
          if (!options.onlyInteger) {
            pattern += "(\\.\\d+)?";
          }
          pattern += "$";
          if (!new RegExp(pattern).test(value)) {
            return options.message || options.notValid || this.notValid || this.message || "must be a valid number";
          }
        }

        // Coerce the value to a number unless we're being strict.
        if (options.noStrings !== true && v.isString(value) && !v.isEmpty(value)) {
          value = +value;
        }

        // If it's not a number we shouldn't continue since it will compare it.
        if (!v.isNumber(value)) {
          return options.message || options.notValid || this.notValid || this.message || "is not a number";
        }

        // Same logic as above, sort of. Don't bother with comparisons if this
        // doesn't pass.
        if (options.onlyInteger && !v.isInteger(value)) {
          return options.message || options.notInteger || this.notInteger || this.message || "must be an integer";
        }
        for (name in checks) {
          count = options[name];
          if (v.isNumber(count) && !checks[name](value, count)) {
            // This picks the default message if specified
            // For example the greaterThan check uses the message from
            // this.notGreaterThan so we capitalize the name and prepend "not"
            var key = "not" + v.capitalize(name);
            var msg = options[key] || this[key] || this.message || "must be %{type} %{count}";
            errors.push(v.format(msg, {
              count: count,
              type: prettify(name)
            }));
          }
        }
        if (options.odd && value % 2 !== 1) {
          errors.push(options.notOdd || this.notOdd || this.message || "must be odd");
        }
        if (options.even && value % 2 !== 0) {
          errors.push(options.notEven || this.notEven || this.message || "must be even");
        }
        if (errors.length) {
          return options.message || errors;
        }
      },
      datetime: v.extend(function (value, options) {
        if (!v.isFunction(this.parse) || !v.isFunction(this.format)) {
          throw new Error("Both the parse and format functions needs to be set to use the datetime/date validator");
        }

        // Empty values are fine
        if (!v.isDefined(value)) {
          return;
        }
        options = v.extend({}, this.options, options);
        var err,
          errors = [],
          earliest = options.earliest ? this.parse(options.earliest, options) : NaN,
          latest = options.latest ? this.parse(options.latest, options) : NaN;
        value = this.parse(value, options);

        // 86400000 is the number of milliseconds in a day, this is used to remove
        // the time from the date
        if (isNaN(value) || options.dateOnly && value % 86400000 !== 0) {
          err = options.notValid || options.message || this.notValid || "must be a valid date";
          return v.format(err, {
            value: arguments[0]
          });
        }
        if (!isNaN(earliest) && value < earliest) {
          err = options.tooEarly || options.message || this.tooEarly || "must be no earlier than %{date}";
          err = v.format(err, {
            value: this.format(value, options),
            date: this.format(earliest, options)
          });
          errors.push(err);
        }
        if (!isNaN(latest) && value > latest) {
          err = options.tooLate || options.message || this.tooLate || "must be no later than %{date}";
          err = v.format(err, {
            date: this.format(latest, options),
            value: this.format(value, options)
          });
          errors.push(err);
        }
        if (errors.length) {
          return v.unique(errors);
        }
      }, {
        parse: null,
        format: null
      }),
      date: function (value, options) {
        options = v.extend({}, options, {
          dateOnly: true
        });
        return v.validators.datetime.call(v.validators.datetime, value, options);
      },
      format: function (value, options) {
        if (v.isString(options) || options instanceof RegExp) {
          options = {
            pattern: options
          };
        }
        options = v.extend({}, this.options, options);
        var message = options.message || this.message || "is invalid",
          pattern = options.pattern,
          match;

        // Empty values are allowed
        if (!v.isDefined(value)) {
          return;
        }
        if (!v.isString(value)) {
          return message;
        }
        if (v.isString(pattern)) {
          pattern = new RegExp(options.pattern, options.flags);
        }
        match = pattern.exec(value);
        if (!match || match[0].length != value.length) {
          return message;
        }
      },
      inclusion: function (value, options) {
        // Empty values are fine
        if (!v.isDefined(value)) {
          return;
        }
        if (v.isArray(options)) {
          options = {
            within: options
          };
        }
        options = v.extend({}, this.options, options);
        if (v.contains(options.within, value)) {
          return;
        }
        var message = options.message || this.message || "^%{value} is not included in the list";
        return v.format(message, {
          value: value
        });
      },
      exclusion: function (value, options) {
        // Empty values are fine
        if (!v.isDefined(value)) {
          return;
        }
        if (v.isArray(options)) {
          options = {
            within: options
          };
        }
        options = v.extend({}, this.options, options);
        if (!v.contains(options.within, value)) {
          return;
        }
        var message = options.message || this.message || "^%{value} is restricted";
        if (v.isString(options.within[value])) {
          value = options.within[value];
        }
        return v.format(message, {
          value: value
        });
      },
      email: v.extend(function (value, options) {
        options = v.extend({}, this.options, options);
        var message = options.message || this.message || "is not a valid email";
        // Empty values are fine
        if (!v.isDefined(value)) {
          return;
        }
        if (!v.isString(value)) {
          return message;
        }
        if (!this.PATTERN.exec(value)) {
          return message;
        }
      }, {
        PATTERN: /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i
      }),
      equality: function (value, options, attribute, attributes, globalOptions) {
        if (!v.isDefined(value)) {
          return;
        }
        if (v.isString(options)) {
          options = {
            attribute: options
          };
        }
        options = v.extend({}, this.options, options);
        var message = options.message || this.message || "is not equal to %{attribute}";
        if (v.isEmpty(options.attribute) || !v.isString(options.attribute)) {
          throw new Error("The attribute must be a non empty string");
        }
        var otherValue = v.getDeepObjectValue(attributes, options.attribute),
          comparator = options.comparator || function (v1, v2) {
            return v1 === v2;
          },
          prettify = options.prettify || globalOptions && globalOptions.prettify || v.prettify;
        if (!comparator(value, otherValue, options, attribute, attributes)) {
          return v.format(message, {
            attribute: prettify(options.attribute)
          });
        }
      },
      // A URL validator that is used to validate URLs with the ability to
      // restrict schemes and some domains.
      url: function (value, options) {
        if (!v.isDefined(value)) {
          return;
        }
        options = v.extend({}, this.options, options);
        var message = options.message || this.message || "is not a valid url",
          schemes = options.schemes || this.schemes || ['http', 'https'],
          allowLocal = options.allowLocal || this.allowLocal || false,
          allowDataUrl = options.allowDataUrl || this.allowDataUrl || false;
        if (!v.isString(value)) {
          return message;
        }

        // https://gist.github.com/dperini/729294
        var regex = "^" +
        // protocol identifier
        "(?:(?:" + schemes.join("|") + ")://)" +
        // user:pass authentication
        "(?:\\S+(?::\\S*)?@)?" + "(?:";
        var tld = "(?:\\.(?:[a-z\\u00a1-\\uffff]{2,}))";
        if (allowLocal) {
          tld += "?";
        } else {
          regex +=
          // IP address exclusion
          // private & local networks
          "(?!(?:10|127)(?:\\.\\d{1,3}){3})" + "(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})" + "(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})";
        }
        regex +=
        // IP address dotted notation octets
        // excludes loopback network 0.0.0.0
        // excludes reserved space >= 224.0.0.0
        // excludes network & broacast addresses
        // (first & last IP address of each class)
        "(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])" + "(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}" + "(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))" + "|" +
        // host name
        "(?:(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)" +
        // domain name
        "(?:\\.(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)*" + tld + ")" +
        // port number
        "(?::\\d{2,5})?" +
        // resource path
        "(?:[/?#]\\S*)?" + "$";
        if (allowDataUrl) {
          // RFC 2397
          var mediaType = "\\w+\\/[-+.\\w]+(?:;[\\w=]+)*";
          var urlchar = "[A-Za-z0-9-_.!~\\*'();\\/?:@&=+$,%]*";
          var dataurl = "data:(?:" + mediaType + ")?(?:;base64)?," + urlchar;
          regex = "(?:" + regex + ")|(?:^" + dataurl + "$)";
        }
        var PATTERN = new RegExp(regex, 'i');
        if (!PATTERN.exec(value)) {
          return message;
        }
      },
      type: v.extend(function (value, originalOptions, attribute, attributes, globalOptions) {
        if (v.isString(originalOptions)) {
          originalOptions = {
            type: originalOptions
          };
        }
        if (!v.isDefined(value)) {
          return;
        }
        var options = v.extend({}, this.options, originalOptions);
        var type = options.type;
        if (!v.isDefined(type)) {
          throw new Error("No type was specified");
        }
        var check;
        if (v.isFunction(type)) {
          check = type;
        } else {
          check = this.types[type];
        }
        if (!v.isFunction(check)) {
          throw new Error("validate.validators.type.types." + type + " must be a function.");
        }
        if (!check(value, options, attribute, attributes, globalOptions)) {
          var message = originalOptions.message || this.messages[type] || this.message || options.message || (v.isFunction(type) ? "must be of the correct type" : "must be of type %{type}");
          if (v.isFunction(message)) {
            message = message(value, originalOptions, attribute, attributes, globalOptions);
          }
          return v.format(message, {
            attribute: v.prettify(attribute),
            type: type
          });
        }
      }, {
        types: {
          object: function (value) {
            return v.isObject(value) && !v.isArray(value);
          },
          array: v.isArray,
          integer: v.isInteger,
          number: v.isNumber,
          string: v.isString,
          date: v.isDate,
          boolean: v.isBoolean
        },
        messages: {}
      })
    };
    validate.formatters = {
      detailed: function (errors) {
        return errors;
      },
      flat: v.flattenErrorsToArray,
      grouped: function (errors) {
        var attr;
        errors = v.groupErrorsByAttribute(errors);
        for (attr in errors) {
          errors[attr] = v.flattenErrorsToArray(errors[attr]);
        }
        return errors;
      },
      constraint: function (errors) {
        var attr;
        errors = v.groupErrorsByAttribute(errors);
        for (attr in errors) {
          errors[attr] = errors[attr].map(function (result) {
            return result.validator;
          }).sort();
        }
        return errors;
      }
    };
    validate.exposeModule(validate, this, exports, module, define);
  }).call(commonjsGlobal, /* istanbul ignore next */exports, /* istanbul ignore next */module, null);
});
var validation = createCommonjsModule(function (module) {
  (() => {
    var r = {
        26: (r, t, e) => {
          function n(r) {
            return n = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (r) {
              return typeof r;
            } : function (r) {
              return r && "function" == typeof Symbol && r.constructor === Symbol && r !== Symbol.prototype ? "symbol" : typeof r;
            }, n(r);
          }
          function o(r, t) {
            var e = Object.keys(r);
            if (Object.getOwnPropertySymbols) {
              var n = Object.getOwnPropertySymbols(r);
              t && (n = n.filter(function (t) {
                return Object.getOwnPropertyDescriptor(r, t).enumerable;
              })), e.push.apply(e, n);
            }
            return e;
          }
          function u(r) {
            for (var t = 1; t < arguments.length; t++) {
              var e = null != arguments[t] ? arguments[t] : {};
              t % 2 ? o(Object(e), !0).forEach(function (t) {
                var o, u, i;
                o = r, u = t, i = e[t], (u = function (r) {
                  var t = function (r, t) {
                    if ("object" !== n(r) || null === r) return r;
                    var e = r[Symbol.toPrimitive];
                    if (void 0 !== e) {
                      var o = e.call(r, "string");
                      if ("object" !== n(o)) return o;
                      throw new TypeError("@@toPrimitive must return a primitive value.");
                    }
                    return String(r);
                  }(r);
                  return "symbol" === n(t) ? t : String(t);
                }(u)) in o ? Object.defineProperty(o, u, {
                  value: i,
                  enumerable: !0,
                  configurable: !0,
                  writable: !0
                }) : o[u] = i;
              }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(r, Object.getOwnPropertyDescriptors(e)) : o(Object(e)).forEach(function (t) {
                Object.defineProperty(r, t, Object.getOwnPropertyDescriptor(e, t));
              });
            }
            return r;
          }
          var i = e(989),
            a = e(527),
            l = e(362),
            p = e(642),
            s = e(474),
            f = e(972),
            c = {
              required: i,
              minLength: a,
              maxLength: l,
              lessOrEquals: p,
              pattern: s
            };
          r.exports = {
            run: function (r, t) {
              for (var e, n, o, i, a = null, l = 0; l < t.validators.length && null === a;) {
                var p = u({}, t.validators[l]);
                e = p.name, n = p.value, o = t.errorMessages[p.error_message], a = f(c[e], r, n, o), (i = p).error_message = o, l += 1;
              }
              return null === a ? null : i;
            }
          };
        },
        659: (r, t, e) => {
          r.exports = e(26);
        },
        642: (r, t, e) => {
          var n = e(75),
            o = {
              run: function (r, t) {
                return n({
                  input: parseInt(r)
                }, {
                  input: {
                    numericality: {
                      noStrings: !0,
                      lessThanOrEqualTo: t
                    }
                  }
                }, {
                  format: "flat"
                });
              }
            };
          r.exports = o;
        },
        362: (r, t, e) => {
          var n = e(75),
            o = {
              run: function (r, t) {
                return n({
                  input: r
                }, {
                  input: {
                    length: {
                      maximum: t
                    }
                  }
                }, {
                  format: "flat"
                });
              }
            };
          r.exports = o;
        },
        527: (r, t, e) => {
          var n = e(75),
            o = {
              run: function (r, t) {
                return n({
                  input: r
                }, {
                  input: {
                    length: {
                      minimum: t
                    }
                  }
                }, {
                  format: "flat"
                });
              }
            };
          r.exports = o;
        },
        474: (r, t, e) => {
          var n = e(75),
            o = {
              run: function (r, t) {
                return n({
                  input: r
                }, {
                  input: {
                    format: t
                  }
                }, {
                  format: "flat"
                });
              }
            };
          r.exports = o;
        },
        989: (r, t, e) => {
          var n = e(75),
            o = {
              run: function (r, t) {
                return n({
                  input: r
                }, {
                  input: {
                    presence: {
                      allowEmpty: !t
                    }
                  }
                }, {
                  format: "flat"
                });
              }
            };
          r.exports = o;
        },
        972: r => {
          r.exports = function (r, t, e) {
            return void 0 === r.run(t, e) ? null : r;
          };
        },
        75: r => {
          r.exports = validate;
        }
      },
      t = {},
      e = function e(n) {
        var o = t[n];
        if (void 0 !== o) return o.exports;
        var u = t[n] = {
          exports: {}
        };
        return r[n](u, u.exports, e), u.exports;
      }(659);
    module.exports = e;
  })();
});
var frontendValidation = createCommonjsModule(function (module, exports) {
  // eslint-disable-next-line no-var

  module.exports = validation;
  exports = module.exports;
  exports.Validation = validation;
});
const escapeProxy = typeof window !== 'undefined' && encodeURI ? encodeURI : escape;
const objectDecorator = result => _objectSpread({
  isValid: result === null
}, result);
const snackbarColor = new Map();

// values to validate the las props with the new props
snackbarColor.set('error', 'red');
snackbarColor.set('warning', 'orange');
snackbarColor.set('success', 'green');
snackbarColor.set('neutral', 'accent');
snackbarColor.set('generic', 'gray');
// if the back return the color this values is to add the color to the map
snackbarColor.set('red', 'red');
snackbarColor.set('orange', 'orange');
snackbarColor.set('green', 'green');
snackbarColor.set('accent', 'accent');
snackbarColor.set('gray', 'gray');

/**
 * @description: this function validate the kind of messages and return the color
 * @param {string} type: type to validate
 * @returns the color to the messages
 */
const validateSnackbarColor = type => snackbarColor.get(type) || type;

/**
 * @description: this function generate the key to the component
 * @param {*} data: data to find the key
 * @returns the key to the component
 */
const generateKeyToComponent = data => {
  var _data$title, _data$title2, _data$title3, _data$label;
  const title = (data === null || data === void 0 || (_data$title = data.title) === null || _data$title === void 0 || (_data$title = _data$title.values) === null || _data$title === void 0 || (_data$title = _data$title.key) === null || _data$title === void 0 ? void 0 : _data$title.value) || (data === null || data === void 0 || (_data$title2 = data.title) === null || _data$title2 === void 0 || (_data$title2 = _data$title2.values) === null || _data$title2 === void 0 || (_data$title2 = _data$title2.key) === null || _data$title2 === void 0 ? void 0 : _data$title2.text) || (data === null || data === void 0 || (_data$title3 = data.title) === null || _data$title3 === void 0 ? void 0 : _data$title3.text) || (data === null || data === void 0 ? void 0 : data.title);
  const returnValue = (data === null || data === void 0 ? void 0 : data.key) || title || (data === null || data === void 0 ? void 0 : data.id) || (data === null || data === void 0 || (_data$label = data.label) === null || _data$label === void 0 ? void 0 : _data$label.text) || (data === null || data === void 0 ? void 0 : data.text) || (data === null || data === void 0 ? void 0 : data.subtitle) || (data === null || data === void 0 ? void 0 : data.upper_label) || (data === null || data === void 0 ? void 0 : data.description);
  return returnValue;
};
const validateItemOrProductId = value => objectDecorator(frontendValidation.Validation.run(escapeProxy(value), {
  errorMessages: {
    ITEM_ID_REQUIRED: 'ITEM_ID_REQUIRED',
    ITEM_ID_BAD_FORMAT: 'ITEM_ID_BAD_FORMAT'
  },
  validators: [{
    name: 'required',
    value: true,
    errorMessage: 'ITEM_ID_REQUIRED'
  }, {
    name: 'pattern',
    value: '([m|M][a-zA-Z]{2}[u|U]?)([-]?)([0-9]+)',
    errorMessage: 'ITEM_ID_BAD_FORMAT'
  }]
}));
const validateNumber = value => objectDecorator(frontendValidation.Validation.run(escapeProxy(value), {
  errorMessages: {
    NUMBER_BAD_FORMAT: 'NUMBER_BAD_FORMAT'
  },
  validators: [{
    name: 'pattern',
    value: '[0-9]+',
    errorMessage: 'NUMBER_BAD_FORMAT'
  }]
}));
const validateString = value => objectDecorator(frontendValidation.Validation.run(escapeProxy(value), {
  errorMessages: {
    STRING_BAD_FORMAT: 'STRING_BAD_FORMAT'
  },
  validators: [{
    name: 'pattern',
    value: '\\w+|\\|',
    errorMessage: 'STRING_BAD_FORMAT'
  }]
}));
const validateBoolean = value => objectDecorator(frontendValidation.Validation.run(escapeProxy(value), {
  errorMessages: {
    BAD_FORMAT: 'BOOLEAN_BAD_FORMAT'
  },
  validators: [{
    name: 'pattern',
    value: 'true|false',
    errorMessage: 'BOOLEAN_BAD_FORMAT'
  }]
}));

// use an environment variable to know where we are so webpack is able to remove the unused path.
var global$1 = typeof window === 'object' ? window : global;
export { ANDES, ComponentByKey, CurlGenerator, DiscountFontSize, EXP_THREE_COLS_VIP_LAYOUT, EXP_UPP_LAYOUT, FEATURE_ENABLED, ONE_HOUR, ONE_MINUTE, ONE_SECOND, PUSH_NOTIFICATION_BOOKMARK_MODAL, PUSH_NOTIFICATION_DECLINE_ACTION, PUSH_NOTIFICATION_QUESTION_MODAL, PriceFontSize, addEllipsisEnd, analytics, applyClasses, arrayIncludes, backgroundColor, canUseDOM$1 as canUseDOM, categoriesFormatter, categoryFormatter, colornames, compose, constants, corsOptions, corsOptionsDevelop, disabled, dispatchAddToCardShopsInfo, dispatchSellerPixelsInfo, doneHandler, entries, familynames, forEach, generateKeyToComponent, getAllIcons, getCoachCardTooltipsProps, getCoachTooltipsProps, getCookie, getCookieValue, getDisplayName, getEventClickShippingCalculateTrack, getEventClickShippingValuesTrack, getExpiration, getFormattedTrackingData, getLocationForHash, getParentMessageRedirectFrom, getPromiseTrack, getShippingCoachMarkTrack, getShopFeatures, getShopsHeaders, getSimilarLinkMessageRedirectFrom, getSimilarMessageRedirectFrom, getTestEnvScopeParams, getTestObjectScopeParamsFromUrl, getTestURLScopeParams, getTimerText, global$1 as global, hasBlockedNotifications, hasGrantedNotifactions, hyphenatedbem, idToUrl, isBuyboxShown, isMshops, isOfficialStoreHeaderVisible, isPaymentMethodsVisible, isPushNotificationsPermissionModal, isReviewsRowVisible, isSellerDataVisible, isSellerInfoVisible, isSupported, isSupportedByDefault, itemIdToVipFormat, margins, maxCharsDevice, meliData, messageFormatter, messageSeeMore, messagesErrorScreen, messagesNotFound, onIframeModalResize, privateMethodTest, renderWithSvgIcons, resetCookieCompats, runSchedulerTask, runSchedulerTaskPromise, sanitizeMshopsHeaders, sanitizedData, setCookie, sizenames, ssrOnly, stringIncludes, supportsHistory, supportsWebShareApi, targetHostnameValidator, textAlignment, trackEvent, trackEventOnComponentPrint, trackEventPromise, trackEventSync, trackEventWithCustomField, trackEventWithCustomFields, trackGtmEvent, trackPage, trackQuantityEvent, transformActionTarget, updateComponentPositionWhenSticky, updateCookieCompats, validateBoolean, validateItemOrProductId, validateNumber, validateSnackbarColor, validateString, vipFormatToItemId, whenIdle, whenVisible, withMappedProps };
